import { z } from 'zod';

export const DEFAULT_API_BASE_URL = 'https://api.altiosbuilders.com';

export class ApiError extends Error {
  readonly status: number;
  readonly payload: unknown;

  constructor(message: string, status: number, payload: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.payload = payload;
  }
}

export type ApiClient = ReturnType<typeof createApiClient>;

export function createApiClient(baseUrl = DEFAULT_API_BASE_URL) {
  const normalizedBase = baseUrl.replace(/\/$/, '');

  async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
    const response = await fetch(`${normalizedBase}${path}`, {
      credentials: 'include',
      ...init,
      headers: {
        Accept: 'application/json',
        ...(init.body && !(init.body instanceof FormData) ? { 'Content-Type': 'application/json' } : {}),
        ...init.headers
      }
    });

    const contentType = response.headers.get('content-type') || '';
    const payload: unknown = contentType.includes('application/json')
      ? await response.json().catch(() => null)
      : await response.text().catch(() => '');

    if (!response.ok) {
      const objectPayload = payload && typeof payload === 'object'
        ? payload as Record<string, unknown>
        : null;
      const message = objectPayload && typeof objectPayload.error === 'string'
        ? objectPayload.error
        : objectPayload?.error && typeof objectPayload.error === 'object' && typeof (objectPayload.error as Record<string, unknown>).message === 'string'
          ? String((objectPayload.error as Record<string, unknown>).message)
          : `Request failed (${response.status})`;
      throw new ApiError(message, response.status, payload);
    }

    return payload as T;
  }

  return {
    get: <T>(path: string) => request<T>(path),
    post: <T>(path: string, body: unknown) => request<T>(path, {
      method: 'POST',
      body: JSON.stringify(body)
    }),
    postForm: <T>(path: string, body: FormData) => request<T>(path, {
      method: 'POST',
      body
    }),
    patch: <T>(path: string, body: unknown) => request<T>(path, {
      method: 'PATCH',
      body: JSON.stringify(body)
    }),
    delete: <T>(path: string, body?: unknown) => request<T>(path, {
      method: 'DELETE',
      ...(body === undefined ? {} : { body: JSON.stringify(body) })
    })
  };
}

const nullableString = z.string().nullish();
const nullableNumber = z.number().nullish();

export const CustomerSchema = z.object({
  id: z.string(),
  name: nullableString,
  first_name: nullableString,
  last_name: nullableString,
  email: nullableString,
  phone: nullableString,
  company_name: nullableString,
  address_line: nullableString,
  city: nullableString,
  state: nullableString,
  zip: nullableString,
  country: nullableString,
  archive_status: nullableString,
  archived_at: nullableString,
  archived_by: nullableString,
  zoho_customer_id: nullableString,
  zoho_sync_status: nullableString,
  zoho_sync_error: nullableString,
  zoho_synced_at: nullableString,
  created_at: nullableString,
  updated_at: nullableString
}).passthrough();

export type Customer = z.infer<typeof CustomerSchema>;

export const CustomersResponseSchema = z.object({
  customers: z.array(CustomerSchema).default([])
}).passthrough();

export const ProjectSchema = z.object({
  id: z.string(),
  name: nullableString,
  stage: nullableString,
  status: nullableString,
  phase: nullableString,
  current_phase: nullableString,
  project_type: nullableString,
  project_number: nullableString,
  number: nullableString,
  jobsite_address: nullableString,
  first_name: nullableString,
  last_name: nullableString,
  customer_email: nullableString,
  customer_id: nullableString,
  archive_status: nullableString,
  archived_at: nullableString,
  nc_tax_treatment: nullableString,
  jobsite_county: nullableString,
  e589ci_status: nullableString,
  e589ci_document_id: nullableString,
  e589ci_exception_reason: nullableString,
  contract_price_cents: nullableNumber,
  estimate_count: nullableNumber,
  invoice_count: nullableNumber,
  open_tasks: nullableNumber,
  estimated_start_date: nullableString,
  estimated_completion_date: nullableString,
  phase_updated_at: nullableString,
  zoho_project_id: nullableString,
  created_at: nullableString,
  updated_at: nullableString
}).passthrough();

export type Project = z.infer<typeof ProjectSchema>;

// Capital-improvement affidavit status is independent of agreement execution.
// "Received" means a signed affidavit is on file; an authorized, documented
// "Not Required" exception is also resolved. Neither status changes contracts.
export function needsCapitalImprovementAffidavit(project: Pick<Project,
  'nc_tax_treatment' | 'e589ci_status' | 'e589ci_exception_reason'>): boolean {
  if (String(project.nc_tax_treatment || '').trim().toLowerCase() !== 'capital improvement') return false;
  const status = String(project.e589ci_status || '').trim().toLowerCase();
  if (status === 'received') return false;
  if (status === 'not required' && String(project.e589ci_exception_reason || '').trim()) return false;
  return true;
}

export const ProjectManageDataSchema = z.object({
  project: ProjectSchema,
  counts: z.object({
    tasks: z.number().default(0),
    selections: z.number().default(0),
    customerSelections: z.number().default(0),
    documents: z.number().default(0),
    invoices: z.number().default(0),
    payments: z.number().default(0)
  }).passthrough(),
  customerReassignment: z.object({
    allowed: z.boolean().default(false),
    blockers: z.array(z.object({
      code: z.string().nullish(),
      message: z.string().nullish()
    }).passthrough()).default([])
  }).passthrough()
}).passthrough();

export type ProjectManageData = z.infer<typeof ProjectManageDataSchema>;

export const ProjectsResponseSchema = z.object({
  projects: z.array(ProjectSchema).default([])
}).passthrough();

export const EstimateSchema = z.object({
  id: z.string(),
  project_id: nullableString,
  project_name: nullableString,
  title: nullableString,
  name: nullableString,
  status: nullableString,
  total: nullableNumber,
  line_total: nullableNumber,
  line_count: nullableNumber,
  estimate_total_cents: nullableNumber,
  scope_of_work: nullableString,
  internal_notes: nullableString,
  estimate_kind: nullableString,
  current_version: nullableNumber,
  content_version: z.union([z.string(), z.number()]).nullish(),
  conversion_status: nullableString,
  is_current: z.boolean().optional(),
  is_in_use: z.boolean().optional(),
  can_delete: z.boolean().optional(),
  usage_labels: z.array(z.string()).optional(),
  delete_blockers: z.array(z.string()).optional(),
  contract_count: nullableNumber,
  selection_count: nullableNumber,
  approval_count: nullableNumber,
  approval: z.object({ status: nullableString }).nullish(),
  isStale: z.boolean().optional(),
  created_at: nullableString,
  updated_at: nullableString
}).passthrough();

export const EstimateLineSchema = z.object({
  id: z.string(),
  estimate_version_id: nullableString,
  position: nullableNumber,
  lineage_id: nullableString,
  description: nullableString,
  quantity: nullableNumber,
  quantity_millis: nullableNumber,
  usage_unit: nullableString,
  rate: nullableNumber,
  unit_rate_cents: nullableNumber,
  total: nullableNumber,
  line_total_cents: nullableNumber,
  account_code: nullableNumber,
  zoho_item_id: nullableString,
  assigned_role: nullableString,
  mapping_status: nullableString,
  requires_selection: z.union([z.boolean(), z.number()]).nullish(),
  source_type: nullableString,
  source_reference: nullableString,
  price_book_item_id: nullableString,
  price_book_tier: nullableString,
  price_book_base_quantity_millis: nullableNumber,
  price_book_waste_percent: nullableNumber,
  price_book_pricing_snapshot_json: nullableString,
  content_version: z.union([z.string(), z.number()]).nullish(),
  approval: z.object({ status: nullableString }).nullish(),
  isStale: z.boolean().optional(),
  created_at: nullableString,
  updated_at: nullableString
}).passthrough();

export const EstimateDetailResponseSchema = z.object({
  estimate: EstimateSchema,
  lineItems: z.array(EstimateLineSchema).default([])
}).passthrough();

export const ConversionCheckSchema = z.object({
  label: z.string(),
  pass: z.boolean(),
  detail: z.unknown().optional()
}).passthrough();

export const ConversionGateSchema = z.object({
  found: z.boolean().default(true),
  ready: z.boolean().default(false),
  checks: z.array(ConversionCheckSchema).default([])
}).passthrough();

export const PriceBookTierSchema = z.object({
  base_unit_cost: nullableNumber,
  adjusted_base_unit_cost: nullableNumber,
  sell_rate: nullableNumber,
  source_name: nullableString,
  source_url: nullableString,
  effective_at: nullableString
}).passthrough();

export const PriceBookItemSchema = z.object({
  id: z.string(),
  item_code: nullableString,
  name: nullableString,
  description: nullableString,
  category: nullableString,
  cost_type: nullableString,
  usage_unit: nullableString,
  account_code: nullableNumber,
  account_name: nullableString,
  status: nullableString,
  fixed_base_rate: nullableNumber,
  fixed_sell_rate: nullableNumber,
  waste_percent: nullableNumber,
  requires_selection: z.boolean().optional(),
  accounting_mapping_status: nullableString,
  zoho_item_id: nullableString,
  zoho_item_name: nullableString,
  tiers: z.object({
    Basic: PriceBookTierSchema.optional(),
    Standard: PriceBookTierSchema.optional(),
    Premium: PriceBookTierSchema.optional()
  }).partial().optional()
}).passthrough();

export const ZohoItemSchema = z.object({
  item_id: z.union([z.string(), z.number().transform(String)]),
  name: nullableString,
  item_name: nullableString,
  sku: nullableString,
  item_code: nullableString,
  status: nullableString,
  unit: nullableString,
  unit_name: nullableString,
  description: nullableString
}).passthrough();

export const PriceBookSchema = z.object({
  settings: z.object({
    default_material_waste_percent: nullableNumber,
    material_overhead_percent: nullableNumber,
    material_profit_percent: nullableNumber,
    material_global_adjustment_percent: nullableNumber,
    labor_global_adjustment_percent: nullableNumber,
    op_calculation_method: nullableString,
    default_material_tier: nullableString
  }).passthrough(),
  items: z.array(PriceBookItemSchema).default([]),
  accounts: z.array(z.object({
    account_code: z.union([z.number(), z.string()]),
    account_name: nullableString,
    account_type: nullableString,
    parent_account: nullableString
  }).passthrough()).default([]),
  due_checks: z.array(z.record(z.string(), z.unknown())).default([])
}).passthrough();

export const InvoiceSchema = z.object({
  id: z.string(),
  project_id: nullableString,
  status: nullableString,
  total: nullableNumber,
  total_cents: nullableNumber,
  zoho_invoice_id: nullableString,
  created_at: nullableString,
  updated_at: nullableString
}).passthrough();

export const ContractSchema = z.object({
  id: z.string(),
  project_id: nullableString,
  customer_id: nullableString,
  title: nullableString,
  document_type: nullableString,
  version: nullableNumber,
  status: nullableString,
  total_amount: nullableNumber,
  total_amount_cents: nullableNumber,
  source_estimate_id: nullableString,
  source_estimate_version_id: nullableString,
  estimated_start_date: nullableString,
  estimated_completion_date: nullableString,
  sent_for_signature_at: nullableString,
  customer_signed_at: nullableString,
  signer_name: nullableString,
  signer_email: nullableString,
  contractor_signed_at: nullableString,
  execution_method: nullableString,
  execution_date: nullableString,
  executed_at: nullableString,
  executed_by: nullableString,
  executed_document_id: nullableString,
  baseline_locked_at: nullableString,
  signed_at: nullableString,
  signed_date: nullableString,
  signature_request_id: nullableString,
  signature_request_status: nullableString,
  signature_document_id: nullableString,
  signature_document_sha256: nullableString,
  signature_consent_text: nullableString,
  signature_consent_version: nullableString,
  signature_viewed_at: nullableString,
  signature_recipient_email: nullableString,
  created_at: nullableString,
  updated_at: nullableString
}).passthrough();

export const ContractGateCheckSchema = z.object({
  name: z.string(),
  passed: z.boolean()
}).passthrough();

export const ContractConversionGateSchema = z.object({
  passed: z.boolean(),
  checks: z.array(ContractGateCheckSchema).default([])
}).passthrough();

export type ContractGateCheck = z.infer<typeof ContractGateCheckSchema>;
export type ContractConversionGate = z.infer<typeof ContractConversionGateSchema>;

export const TaskSchema = z.object({
  id: z.string(),
  project_id: nullableString,
  project_name: nullableString,
  title: nullableString,
  customer_title: nullableString,
  customer_instructions: nullableString,
  details: nullableString,
  status: nullableString,
  assigned_to: nullableString,
  due_date: nullableString,
  start_date: nullableString,
  end_date: nullableString,
  priority: nullableNumber,
  phase: nullableString,
  work_phase: nullableString,
  task_type: nullableString,
  dependencies_json: nullableString,
  show_on_timeline: z.union([z.boolean(), z.number()]).nullish(),
  portal_visible: z.union([z.boolean(), z.number()]).nullish(),
  phase_progress_approved_at: nullableString,
  phase_progress_approved_by: nullableString,
  plan_source: nullableString,
  plan_run_id: nullableString,
  created_at: nullableString,
  updated_at: nullableString
}).passthrough();

export const CloseoutItemSchema = z.object({
  id: z.string(),
  project_id: z.string(),
  item_type: nullableString,
  reference_id: nullableString,
  label: nullableString,
  status: nullableString,
  notes: nullableString,
  created_at: nullableString,
  updated_at: nullableString
}).passthrough();

export const ProjectCloseoutSchema = z.object({
  id: z.string(),
  project_id: z.string(),
  final_audit_status: nullableString,
  payment_verified: z.union([z.boolean(), z.number()]).nullish(),
  documents_complete: z.union([z.boolean(), z.number()]).nullish(),
  customer_satisfaction_complete: z.union([z.boolean(), z.number()]).nullish(),
  warranty_created: z.union([z.boolean(), z.number()]).nullish(),
  completed_by: nullableString,
  completed_at: nullableString,
  created_at: nullableString,
  updated_at: nullableString
}).passthrough();

export const CloseoutReadinessSchema = z.object({
  readyToComplete: z.boolean().default(false),
  completed: z.boolean().default(false),
  blockers: z.array(z.string()).default([]),
  unresolvedItemCount: z.number().default(0),
  totalItemCount: z.number().default(0)
}).passthrough();

export const CloseoutStateSchema = z.object({
  project: ProjectSchema,
  closeout: ProjectCloseoutSchema.nullish(),
  items: z.array(CloseoutItemSchema).default([]),
  readiness: CloseoutReadinessSchema
}).passthrough();

export const PortalCloseoutSummarySchema = z.object({
  available: z.boolean().default(true),
  finalAuditStatus: z.string().default('Pending'),
  paymentVerified: z.boolean().default(false),
  documentsComplete: z.boolean().default(false),
  customerSatisfactionComplete: z.boolean().default(false),
  warrantyCreated: z.boolean().default(false),
  completedAt: nullableString,
  completed: z.boolean().default(false)
}).passthrough();

export const PortalPhaseProgressItemSchema = z.object({
  id: z.string(),
  title: nullableString,
  status: nullableString,
  assignedTo: nullableString,
  approved: z.boolean().default(false),
  dueDate: nullableString,
  instructions: nullableString
}).passthrough();

export const PortalPhaseProgressGroupSchema = z.object({
  required: z.number().default(0),
  approved: z.number().default(0),
  items: z.array(PortalPhaseProgressItemSchema).default([])
}).passthrough();

export const PortalPhaseProgressSchema = z.object({
  phases: z.record(z.string(), PortalPhaseProgressGroupSchema).default({})
}).passthrough();

export const AppointmentSchema = z.object({
  id: z.string(),
  project_id: z.string(),
  appointment_type: nullableString,
  title: nullableString,
  starts_at: nullableString,
  ends_at: nullableString,
  timezone: nullableString,
  location: nullableString,
  customer_notes: nullableString,
  internal_notes: nullableString,
  status: nullableString,
  proposed_by: nullableString,
  customer_visible: z.union([z.boolean(), z.number()]).nullish(),
  confirmed_at: nullableString,
  confirmed_by: nullableString,
  cancelled_at: nullableString,
  cancelled_by: nullableString,
  reminder_timing: nullableString,
  reminder_delivery: nullableString,
  created_at: nullableString,
  updated_at: nullableString
}).passthrough();

export const ProjectUnavailabilitySchema = z.object({
  id: z.string(),
  project_id: z.string(),
  owner_type: nullableString,
  starts_at: nullableString,
  ends_at: nullableString,
  all_day: z.union([z.boolean(), z.number()]).nullish(),
  note: nullableString,
  status: nullableString,
  created_at: nullableString,
  updated_at: nullableString
}).passthrough();

export const AppointmentEventSchema = z.object({
  id: z.string(),
  appointment_id: nullableString,
  project_id: nullableString,
  event_type: nullableString,
  actor_type: nullableString,
  actor: nullableString,
  from_status: nullableString,
  to_status: nullableString,
  starts_at: nullableString,
  ends_at: nullableString,
  created_at: nullableString
}).passthrough();

export const ProjectCalendarSchema = z.object({
  appointments: z.array(AppointmentSchema).default([]),
  unavailability: z.array(ProjectUnavailabilitySchema).default([]),
  events: z.array(AppointmentEventSchema).default([])
}).passthrough();

export const DocumentSchema = z.object({
  id: z.string(),
  project_id: nullableString,
  customer_id: nullableString,
  contract_id: nullableString,
  change_order_id: nullableString,
  name: nullableString,
  file_name: nullableString,
  original_name: nullableString,
  title: nullableString,
  category: nullableString,
  mime_type: nullableString,
  size_bytes: nullableNumber,
  sha256: nullableString,
  r2_bucket: nullableString,
  r2_key: nullableString,
  status: nullableString,
  media_stage: nullableString,
  location_label: nullableString,
  caption: nullableString,
  is_360: z.union([z.boolean(), z.number()]).nullish(),
  customer_visible: z.union([z.boolean(), z.number()]).nullish(),
  captured_at: nullableString,
  archived_at: nullableString,
  created_at: nullableString,
  updated_at: nullableString
}).passthrough();

export const DocumentsResponseSchema = z.object({
  documents: z.array(DocumentSchema).default([])
}).passthrough();

export const DesignPreviewSchema = z.object({
  id: z.string(),
  project_id: z.string(),
  selection_id: nullableString,
  mode: z.enum(['Photo', 'Concept']),
  title: nullableString,
  prompt: z.string(),
  source_document_id: nullableString,
  drawing_document_id: nullableString,
  render_document_id: nullableString,
  concept_json: nullableString,
  provider: nullableString,
  model: nullableString,
  created_by: nullableString,
  created_at: nullableString,
  updated_at: nullableString,
  selection_description: nullableString,
  selection_room: nullableString,
  source_name: nullableString,
  drawing_name: nullableString,
  render_name: nullableString,
  render_customer_visible: z.union([z.boolean(), z.number()]).default(0),
  staff_recommended: z.union([z.boolean(), z.number()]).default(0),
  final_direction_at: nullableString,
  final_direction_by: nullableString
}).passthrough();

export const SelectionDesignReferenceSchema = z.object({
  id: z.string(),
  project_id: z.string(),
  selection_id: z.string(),
  document_id: z.string(),
  source_role: nullableString,
  note: nullableString,
  created_by: nullableString,
  created_at: nullableString,
  original_name: nullableString,
  caption: nullableString,
  mime_type: nullableString,
  size_bytes: nullableNumber
}).passthrough();

export type SelectionDesignReference = z.infer<typeof SelectionDesignReferenceSchema>;

export const DesignPreviewsResponseSchema = z.object({
  previews: z.array(DesignPreviewSchema).default([])
}).passthrough();

export const PaymentElectionSchema = z.object({
  id: z.string(),
  project_id: nullableString,
  billing_event_type: nullableString,
  billing_event_id: nullableString,
  amount_cents: nullableNumber,
  currency_code: nullableString,
  method_code: nullableString,
  method_label: nullableString,
  elected_by_role: nullableString,
  recorded_via: nullableString,
  recorded_by_email: nullableString,
  notes: nullableString,
  supersedes_election_id: nullableString,
  zoho_invoice_id: nullableString,
  zoho_retainer_invoice_id: nullableString,
  zoho_payment_url: nullableString,
  elected_at: nullableString,
  created_at: nullableString
}).passthrough();

export const MilestoneSchema = z.object({
  id: z.string(),
  project_id: nullableString,
  project_name: nullableString,
  title: nullableString,
  status: nullableString,
  percent_complete: nullableNumber,
  amount_due: nullableNumber,
  amount_due_cents: nullableNumber,
  billing_method: nullableString,
  milestone_type: nullableString,
  zoho_invoice_id: nullableString,
  zoho_retainer_invoice_id: nullableString,
  payment_url: nullableString,
  invoiced_at: nullableString,
  zoho_sync_error: nullableString,
  gate_satisfied_at: nullableString,
  payment_election: PaymentElectionSchema.nullish(),
  payment_method: nullableString,
  payment_method_label: nullableString,
  payment_link_available: z.boolean().optional(),
  created_at: nullableString,
  updated_at: nullableString
}).passthrough();

export const FinancialStatementInvoiceSchema = z.object({
  kind: nullableString,
  id: z.string(),
  totalCents: nullableNumber,
  paidCents: nullableNumber,
  paymentKnown: z.boolean().default(false),
  reference: nullableString,
  date: nullableString,
  status: nullableString,
  title: nullableString,
  percentComplete: nullableNumber,
  total: nullableNumber,
  paid: nullableNumber,
  balance: nullableNumber
}).passthrough();

export const FinancialStatementChangeOrderSchema = z.object({
  id: z.string(),
  title: nullableString,
  amount: nullableNumber,
  date: nullableString
}).passthrough();

export const FinancialSummarySchema = z.object({
  currentEstimatePrice: nullableNumber,
  currentEstimateStatus: nullableString,
  approvedContractAmount: nullableNumber,
  originalContractPrice: nullableNumber,
  approvedChangeOrders: nullableNumber,
  revisedContractTotal: nullableNumber,
  totalInvoiced: nullableNumber,
  totalPaid: nullableNumber,
  outstandingInvoicedBalance: nullableNumber,
  remainingNotInvoiced: nullableNumber,
  leftToPay: nullableNumber,
  currentBalance: nullableNumber,
  nextDueAmount: nullableNumber,
  paymentDataComplete: z.boolean().default(false),
  statement: z.object({
    invoices: z.array(FinancialStatementInvoiceSchema).default([]),
    changeOrders: z.array(FinancialStatementChangeOrderSchema).default([])
  }).default({ invoices: [], changeOrders: [] })
}).passthrough();

export const SelectionOptionSchema = z.object({
  id: z.string(),
  selection_item_id: nullableString,
  description: nullableString,
  product_code: nullableString,
  price_cents: nullableNumber,
  price: nullableNumber,
  position: nullableNumber,
  created_at: nullableString
}).passthrough();

export const SelectionMessageSchema = z.object({
  id: z.string(),
  selection_item_id: nullableString,
  author_role: nullableString,
  author_email: nullableString,
  message_text: nullableString,
  created_at: nullableString
}).passthrough();

export const SelectionProductCandidateSchema = z.object({
  id: z.string(),
  selection_item_id: nullableString,
  search_id: nullableString,
  description: nullableString,
  brand: nullableString,
  product_code: nullableString,
  retailer: nullableString,
  product_url: nullableString,
  image_url: nullableString,
  reference_price_cents: nullableNumber,
  reference_price: nullableNumber,
  match_reason: nullableString,
  position: nullableNumber,
  chosen_at: nullableString,
  chosen_by_role: nullableString,
  created_at: nullableString
}).passthrough();

export const TechnicalReviewRunSchema = z.object({
  id: z.string().optional(),
  project_id: nullableString,
  package_id: nullableString,
  status: nullableString,
  model: nullableString,
  findings_count: nullableNumber,
  error_message: nullableString,
  started_at: nullableString,
  completed_at: nullableString,
  created_at: nullableString,
  updated_at: nullableString
}).passthrough();

export const SelectionPackageSchema = z.object({
  id: z.string(),
  project_id: nullableString,
  version_number: nullableNumber,
  status: nullableString,
  submitted_by_email: nullableString,
  submitted_at: nullableString,
  reviewed_by: nullableString,
  reviewed_at: nullableString,
  review_notes: nullableString,
  approved_at: nullableString,
  item_count: nullableNumber,
  is_current: z.boolean().optional(),
  blocking_review_count: nullableNumber,
  technical_review: TechnicalReviewRunSchema.nullish(),
  created_at: nullableString,
  updated_at: nullableString
}).passthrough();

export const SelectionPackageStateSchema = z.object({
  assigned_count: z.number().default(0),
  complete_count: z.number().default(0),
  remaining_count: z.number().default(0),
  can_submit: z.boolean().default(false),
  incomplete: z.array(z.object({ id: z.string(), description: nullableString }).passthrough()).default([]),
  package: SelectionPackageSchema.nullish()
}).passthrough();

export const SelectionLayoutSchema = z.object({
  status: nullableString,
  required: z.union([z.boolean(), z.number()]).nullish(),
  request_measurements: z.union([z.boolean(), z.number()]).nullish(),
  request_line_drawing: z.union([z.boolean(), z.number()]).nullish(),
  request_rendering: z.union([z.boolean(), z.number()]).nullish(),
  request_details: nullableString,
  document_id: nullableString,
  document_name: nullableString,
  version_label: nullableString,
  provided_method: nullableString,
  approved_at: nullableString,
  updated_at: nullableString
}).passthrough();

export const SelectionSchema = z.object({
  id: z.string(),
  project_id: nullableString,
  project_name: nullableString,
  description: nullableString,
  room: nullableString,
  status: nullableString,
  due_date: nullableString,
  allowance_amount_cents: nullableNumber,
  allowance_amount: nullableNumber,
  effective_allowance_amount_cents: nullableNumber,
  stored_allowance_amount: nullableNumber,
  unit_cost_cents: nullableNumber,
  unit_cost: nullableNumber,
  quantity: nullableNumber,
  builder_notes: nullableString,
  customer_instructions: nullableString,
  required_before_contract: z.union([z.boolean(), z.number()]).nullish(),
  decision_type: z.enum(['product', 'visual', 'layout', 'placement', 'combined']).default('product'),
  selection_template: nullableString,
  needs_product_code: z.union([z.boolean(), z.number()]).default(1),
  needs_finish_choice: z.union([z.boolean(), z.number()]).default(0),
  needs_layout_choice: z.union([z.boolean(), z.number()]).default(0),
  needs_placement_choice: z.union([z.boolean(), z.number()]).default(0),
  needs_dimensions: z.union([z.boolean(), z.number()]).default(0),
  needs_photo_reference: z.union([z.boolean(), z.number()]).default(0),
  allow_ai_product_match: z.union([z.boolean(), z.number()]).default(1),
  allow_ai_visual_examples: z.union([z.boolean(), z.number()]).default(0),
  allow_ai_layout_generator: z.union([z.boolean(), z.number()]).default(0),
  allow_ai_placement_generator: z.union([z.boolean(), z.number()]).default(0),
  staff_incomplete_override: z.union([z.boolean(), z.number()]).default(0),
  customer_finish_choice: nullableString,
  customer_layout_choice: nullableString,
  customer_placement_choice: nullableString,
  customer_dimensions: nullableString,
  customer_visible: z.union([z.boolean(), z.number()]).nullish(),
  version: nullableNumber,
  chosen_option_id: nullableString,
  selected_option: nullableString,
  chosen_at: nullableString,
  customer_product_description: nullableString,
  customer_product_code: nullableString,
  customer_product_url: nullableString,
  customer_submitted_at: nullableString,
  altios_choose: z.union([z.boolean(), z.number()]).nullish(),
  verified_price_cents: nullableNumber,
  verified_price: nullableNumber,
  approved_overage_cents: nullableNumber,
  approved_overage: nullableNumber,
  overage_approved_at: nullableString,
  overage_amount: nullableNumber,
  source_estimate_id: nullableString,
  source_estimate_title: nullableString,
  source_estimate_kind: nullableString,
  source_estimate_version_id: nullableString,
  source_estimate_version_number: nullableNumber,
  current_estimate_version_number: nullableNumber,
  source_lineage_id: nullableString,
  source_description: nullableString,
  source_account_code: z.union([z.string(), z.number().transform(String)]).nullish(),
  source_zoho_item_id: nullableString,
  source_match_key: nullableString,
  source_mapping_status: nullableString,
  source_allowance_amount_cents: nullableNumber,
  resulting_line_item_id: nullableString,
  resulting_lineage_id: nullableString,
  options: z.array(SelectionOptionSchema).default([]),
  messages: z.array(SelectionMessageSchema).default([]),
  product_candidates: z.array(SelectionProductCandidateSchema).default([]),
  created_at: nullableString,
  updated_at: nullableString
}).passthrough();

export const SelectionsResponseSchema = z.object({
  selections: z.array(SelectionSchema).default([]),
  selectionPackage: SelectionPackageStateSchema.nullish(),
  layout: SelectionLayoutSchema.nullish()
}).passthrough();

export type SelectionOption = z.infer<typeof SelectionOptionSchema>;
export type SelectionMessage = z.infer<typeof SelectionMessageSchema>;
export type SelectionProductCandidate = z.infer<typeof SelectionProductCandidateSchema>;
export type SelectionPackage = z.infer<typeof SelectionPackageSchema>;
export type SelectionPackageState = z.infer<typeof SelectionPackageStateSchema>;
export type SelectionLayout = z.infer<typeof SelectionLayoutSchema>;
export type Selection = z.infer<typeof SelectionSchema>;
export type SelectionsResponse = z.infer<typeof SelectionsResponseSchema>;
export const DecisionContextSourceSchema = z.object({
  id: z.string(),
  project_id: z.string(),
  selection_item_id: nullableString,
  source_type: z.string().default('Other'),
  title: nullableString,
  source_name: nullableString,
  excerpt: z.string().default(''),
  char_count: z.number().default(0),
  created_by: nullableString,
  created_at: nullableString
}).passthrough();

export const SelectionContextAnalysisSchema = z.object({
  summary: z.string().default(''),
  productDescription: nullableString,
  productCode: nullableString,
  productUrl: nullableString,
  finishChoice: nullableString,
  layoutChoice: nullableString,
  placementChoice: nullableString,
  dimensions: nullableString,
  customerInstructions: nullableString,
  builderNotes: nullableString,
  unresolvedQuestions: z.array(z.string()).default([]),
  evidence: z.array(z.object({
    contextId: z.string().default(''),
    title: z.string().default(''),
    quote: z.string().default('')
  }).passthrough()).default([])
}).passthrough();

export type DecisionContextSource = z.infer<typeof DecisionContextSourceSchema>;
export type SelectionContextAnalysis = z.infer<typeof SelectionContextAnalysisSchema>;

export const ChangeOrderOptionSchema = z.object({
  id: z.string(),
  change_order_line_id: nullableString,
  description: nullableString,
  product_code: nullableString,
  product_url: nullableString,
  price_cents: nullableNumber,
  price: nullableNumber,
  sort_order: nullableNumber,
  status: nullableString,
  created_at: nullableString,
  updated_at: nullableString
}).passthrough();

export const ChangeOrderLineSchema = z.object({
  id: z.string(),
  change_order_id: nullableString,
  position: nullableNumber,
  description: nullableString,
  quantity_millis: nullableNumber,
  quantity: nullableNumber,
  unit_rate_cents: nullableNumber,
  unit_rate: nullableNumber,
  line_total_cents: nullableNumber,
  line_total: nullableNumber,
  estimated_cost_cents: nullableNumber,
  estimated_cost: nullableNumber,
  requires_selection: z.union([z.boolean(), z.number()]).nullish(),
  selection_status: nullableString,
  chosen_option_id: nullableString,
  customer_product_description: nullableString,
  customer_product_code: nullableString,
  customer_product_url: nullableString,
  verified_price_cents: nullableNumber,
  selected_at: nullableString,
  selected_by: nullableString,
  options: z.array(ChangeOrderOptionSchema).default([]),
  created_at: nullableString,
  updated_at: nullableString
}).passthrough();

export const ChangeOrderSchema = z.object({
  id: z.string(),
  project_id: nullableString,
  customer_id: nullableString,
  title: nullableString,
  description: nullableString,
  number: nullableString,
  change_order_number: nullableString,
  status: nullableString,
  total: nullableNumber,
  total_amount: nullableNumber,
  total_cents: nullableNumber,
  estimated_cost_cents: nullableNumber,
  requested_by: nullableString,
  version: nullableNumber,
  sequence_number: nullableNumber,
  source_contract_id: nullableString,
  issued_at: nullableString,
  jobsite_address_snapshot: nullableString,
  original_contract_amount_cents: nullableNumber,
  original_contract_amount: nullableNumber,
  prior_approved_change_orders_cents: nullableNumber,
  prior_approved_change_orders: nullableNumber,
  revised_contract_amount_cents: nullableNumber,
  revised_contract_amount: nullableNumber,
  schedule_impact_type: nullableString,
  schedule_impact_days: nullableNumber,
  sent_for_signature_at: nullableString,
  customer_signed_at: nullableString,
  customer_signer_name: nullableString,
  customer_signer_email: nullableString,
  contractor_signed_at: nullableString,
  contractor_signer_name: nullableString,
  contractor_signer_email: nullableString,
  execution_method: nullableString,
  executed_at: nullableString,
  executed_by: nullableString,
  executed_document_id: nullableString,
  baseline_locked_at: nullableString,
  customer_approved_at: nullableString,
  customer_approved_by: nullableString,
  owner_approved_at: nullableString,
  owner_approved_by: nullableString,
  zoho_invoice_id: nullableString,
  zoho_sync_error: nullableString,
  payment_url: nullableString,
  payment_election: PaymentElectionSchema.nullish(),
  payment_method: nullableString,
  payment_method_label: nullableString,
  payment_link_available: z.boolean().optional(),
  signature_request_id: nullableString,
  signature_request_status: nullableString,
  signature_document_id: nullableString,
  signature_document_sha256: nullableString,
  signature_consent_text: nullableString,
  signature_consent_version: nullableString,
  signature_viewed_at: nullableString,
  signature_recipient_email: nullableString,
  created_at: nullableString,
  updated_at: nullableString,
  signed_at: nullableString
}).passthrough();

export const ChangeOrderDetailSchema = z.object({
  changeOrder: ChangeOrderSchema,
  lines: z.array(ChangeOrderLineSchema).default([]),
  approval: z.record(z.string(), z.unknown()).nullish()
}).passthrough();

export const ChangeOrdersResponseSchema = z.object({
  changeOrders: z.array(ChangeOrderSchema).default([])
}).passthrough();

export const ProjectFinancialsSchema = z.object({
  originalContractAmountCents: z.number().default(0),
  originalContractAmount: z.number().default(0),
  executedChangeOrdersCount: z.number().default(0),
  executedChangeOrdersAmountCents: z.number().default(0),
  executedChangeOrdersAmount: z.number().default(0),
  revisedContractAmountCents: z.number().default(0),
  revisedContractAmount: z.number().default(0),
  executedChangeOrderEstimatedCostCents: z.number().default(0),
  executedChangeOrderEstimatedCost: z.number().default(0)
}).passthrough();

export const ProjectDetailResponseSchema = z.object({
  project: ProjectSchema,
  estimates: z.array(EstimateSchema).default([]),
  invoices: z.array(InvoiceSchema).default([]),
  contracts: z.array(ContractSchema).default([]),
  tasks: z.array(TaskSchema).default([]),
  documents: z.array(DocumentSchema).default([]),
  designPreviews: z.array(DesignPreviewSchema).default([]),
  designReferences: z.array(SelectionDesignReferenceSchema).default([]),
  milestones: z.array(MilestoneSchema).default([]),
  selections: z.array(SelectionSchema).default([]),
  financials: ProjectFinancialsSchema.default({
    originalContractAmountCents: 0,
    originalContractAmount: 0,
    executedChangeOrdersCount: 0,
    executedChangeOrdersAmountCents: 0,
    executedChangeOrdersAmount: 0,
    revisedContractAmountCents: 0,
    revisedContractAmount: 0,
    executedChangeOrderEstimatedCostCents: 0,
    executedChangeOrderEstimatedCost: 0
  }),
  changeOrders: z.array(ChangeOrderSchema).default([])
}).passthrough();

export type ProjectDetail = z.infer<typeof ProjectDetailResponseSchema>;
export type Estimate = z.infer<typeof EstimateSchema>;
export type EstimateLine = z.infer<typeof EstimateLineSchema>;
export type EstimateDetail = z.infer<typeof EstimateDetailResponseSchema>;
export type ConversionGate = z.infer<typeof ConversionGateSchema>;
export type PriceBook = z.infer<typeof PriceBookSchema>;
export type ZohoItem = z.infer<typeof ZohoItemSchema>;
export type PriceBookItem = z.infer<typeof PriceBookItemSchema>;
export type Invoice = z.infer<typeof InvoiceSchema>;
export type Milestone = z.infer<typeof MilestoneSchema>;
export type PaymentElection = z.infer<typeof PaymentElectionSchema>;
export type FinancialSummary = z.infer<typeof FinancialSummarySchema>;
export type Contract = z.infer<typeof ContractSchema>;
export type ProjectTask = z.infer<typeof TaskSchema>;
export type CloseoutItem = z.infer<typeof CloseoutItemSchema>;
export type ProjectCloseout = z.infer<typeof ProjectCloseoutSchema>;
export type CloseoutReadiness = z.infer<typeof CloseoutReadinessSchema>;
export type CloseoutState = z.infer<typeof CloseoutStateSchema>;
export type PortalCloseoutSummary = z.infer<typeof PortalCloseoutSummarySchema>;
export type PortalPhaseProgress = z.infer<typeof PortalPhaseProgressSchema>;
export type Appointment = z.infer<typeof AppointmentSchema>;
export type ProjectUnavailability = z.infer<typeof ProjectUnavailabilitySchema>;
export type AppointmentEvent = z.infer<typeof AppointmentEventSchema>;
export type ProjectCalendar = z.infer<typeof ProjectCalendarSchema>;
export type ProjectDocument = z.infer<typeof DocumentSchema>;
export type DesignPreview = z.infer<typeof DesignPreviewSchema>;
export type ChangeOrderOption = z.infer<typeof ChangeOrderOptionSchema>;
export type ChangeOrderLine = z.infer<typeof ChangeOrderLineSchema>;
export type ChangeOrder = z.infer<typeof ChangeOrderSchema>;
export type ChangeOrderDetail = z.infer<typeof ChangeOrderDetailSchema>;

export const MessageThreadSchema = z.object({
  kind: z.string(),
  thread_id: z.string(),
  project_id: z.string(),
  project_name: nullableString,
  title: nullableString,
  message_count: nullableNumber,
  last_message_at: nullableString,
  customer_name: nullableString,
  customer_email: nullableString
}).passthrough();

export const MessageThreadsResponseSchema = z.object({
  threads: z.array(MessageThreadSchema).default([])
}).passthrough();

export type MessageThread = z.infer<typeof MessageThreadSchema>;


export type PortalFeedbackPage = 'home' | 'selections' | 'messages' | 'schedule' | 'files' | 'progress' | 'more'
  | 'money/overview' | 'money/estimates' | 'money/agreements' | 'money/changes' | 'money/payments';
export type PortalFeedbackIssue = 'navigation' | 'broken' | 'unclear' | 'steps' | 'idea';
export type PortalFeedbackImpact = 'blocked' | 'slow' | 'idea';

export interface PortalFeedbackGroup {
  page: PortalFeedbackPage;
  issueType: PortalFeedbackIssue;
  reports: number;
  projects: number;
  customers: number;
  blockedReports: number;
  blockedCustomers: number;
  latestAt: string | null;
  examples: { note: string; impact: PortalFeedbackImpact; createdAt: string }[];
}
export interface PortalFeedbackSummary {
  periodDays: number;
  totalReports: number;
  affectedProjects: number;
  affectedCustomers: number;
  groups: PortalFeedbackGroup[];
}
export async function submitPortalFeedback(input: {
  projectId: string;
  email: string;
  page: PortalFeedbackPage;
  issueType: PortalFeedbackIssue;
  impact: PortalFeedbackImpact;
  note: string;
}): Promise<void> {
  await client.post('/api/portal/feedback', input);
}
export async function getPortalFeedbackSummary(): Promise<PortalFeedbackSummary> {
  const raw = await client.get<unknown>('/api/portal-feedback/summary');
  return z.object({
    periodDays: z.number(),
    totalReports: z.number(),
    affectedProjects: z.number(),
    affectedCustomers: z.number(),
    groups: z.array(z.object({
      page: z.string(),
      issueType: z.string(),
      reports: z.number(),
      projects: z.number(),
      customers: z.number(),
      blockedReports: z.number(),
      blockedCustomers: z.number(),
      latestAt: nullableString,
      examples: z.array(z.object({
        note: z.string(),
        impact: z.string(),
        createdAt: z.string()
      })).default([])
    }))
  }).parse(raw) as PortalFeedbackSummary;
}

export const MessageNotificationSchema = z.object({
  id: z.string(),
  project_id: nullableString,
  project_name: nullableString,
  audience: nullableString,
  recipient_email: nullableString,
  event_type: nullableString,
  title: nullableString,
  body: nullableString,
  section: nullableString,
  entity_type: nullableString,
  entity_id: nullableString,
  action_required: nullableNumber,
  priority: nullableString,
  is_read: nullableNumber,
  created_at: nullableString,
  updated_at: nullableString
}).passthrough();

export const MessageNotificationsResponseSchema = z.object({
  notifications: z.array(MessageNotificationSchema).default([]),
  unread_count: z.number().default(0)
}).passthrough();

export type MessageNotification = z.infer<typeof MessageNotificationSchema>;

export const MessagePinSchema = z.object({
  project_id: z.string(),
  thread_type: z.string(),
  thread_id: z.string(),
  pinned_at: nullableString
}).passthrough();

export type MessagePin = z.infer<typeof MessagePinSchema>;

export const ThreadMessageSchema = z.object({
  id: z.string(),
  task_id: nullableString,
  selection_item_id: nullableString,
  project_id: nullableString,
  author_type: nullableString,
  author_role: nullableString,
  author_email: nullableString,
  body: nullableString,
  message_text: nullableString,
  created_at: nullableString
}).passthrough();

export type ThreadMessage = z.infer<typeof ThreadMessageSchema>;

export const MessageAttachmentSchema = z.object({
  message_id: nullableString,
  attachment_id: z.string(),
  document_id: nullableString,
  attachment_category: nullableString,
  caption: nullableString,
  save_to_project_files: nullableNumber,
  attachment_source: nullableString,
  created_at: nullableString,
  original_name: nullableString,
  mime_type: nullableString,
  size_bytes: nullableNumber
}).passthrough();

export type MessageAttachment = z.infer<typeof MessageAttachmentSchema>;

export const MessageVersionSchema = z.object({
  message_id: nullableString,
  editor_type: nullableString,
  editor_identity: nullableString,
  previous_body: nullableString,
  new_body: nullableString,
  created_at: nullableString
}).passthrough();

export const MessageLifecycleStateSchema = z.object({
  edited: z.boolean().default(false),
  hiddenFromCustomer: z.boolean().default(false),
  reactions: z.object({ thumbs_up: z.number().default(0), check: z.number().default(0) }).default({ thumbs_up: 0, check: 0 }),
  myReactions: z.array(z.string()).default([]),
  versions: z.array(MessageVersionSchema).default([]),
  attachments: z.array(MessageAttachmentSchema).default([])
}).passthrough();

export const MessageLifecycleSchema = z.object({
  pinned: z.boolean().default(false),
  messages: z.record(z.string(), MessageLifecycleStateSchema).default({})
}).passthrough();

export type MessageLifecycle = z.infer<typeof MessageLifecycleSchema>;

export const ProjectActivitySchema = z.object({
  id: z.string(),
  project_id: z.string(),
  project_name: nullableString,
  entry_type: nullableString,
  channel: nullableString,
  direction: nullableString,
  contact_role: nullableString,
  contact_name: nullableString,
  subject: nullableString,
  body: nullableString,
  occurred_at: nullableString,
  customer_visible: z.union([z.boolean(), z.number()]).nullish(),
  related_type: nullableString,
  related_id: nullableString,
  source_type: nullableString,
  source_ref: nullableString,
  created_by: nullableString,
  created_at: nullableString,
  updated_at: nullableString
}).passthrough();

export const ProjectActivityResponseSchema = z.object({
  entries: z.array(ProjectActivitySchema).default([])
}).passthrough();

export type ProjectActivity = z.infer<typeof ProjectActivitySchema>;

export const PortalProjectSummarySchema = z.object({
  id: z.string(),
  name: nullableString,
  first_name: nullableString,
  last_name: nullableString,
  customer_email: nullableString
}).passthrough();

export const PortalLoginResponseSchema = z.object({
  isAdmin: z.boolean().optional(),
  customer: z.unknown().optional(),
  projects: z.array(PortalProjectSummarySchema).default([]),
  project: ProjectSchema.optional()
}).passthrough();

export type PortalLoginResponse = z.infer<typeof PortalLoginResponseSchema>;

const runtimeApiBase = import.meta.env
  ? (import.meta.env.VITE_API_BASE_URL ?? DEFAULT_API_BASE_URL)
  : DEFAULT_API_BASE_URL;

const client = createApiClient(runtimeApiBase);

export async function getCustomers(includeArchived = false): Promise<Customer[]> {
  const query = includeArchived ? '?includeArchived=1' : '';
  const raw = await client.get<unknown>(`/api/customers${query}`);
  return CustomersResponseSchema.parse(raw).customers;
}

export async function getCustomer(customerId: string): Promise<Customer> {
  const raw = await client.get<unknown>(`/api/customers/${encodeURIComponent(customerId)}`);
  return z.object({ customer: CustomerSchema }).parse(raw).customer;
}

export async function createCustomer(body: {
  firstName: string;
  lastName: string;
  email?: string;
  phone?: string;
  companyName?: string;
  addressLine?: string;
  city?: string;
  state?: string;
  zip?: string;
  country?: string;
}): Promise<Customer> {
  const raw = await client.post<unknown>('/api/customers', body);
  return z.object({ customer: CustomerSchema }).parse(raw).customer;
}

export async function updateCustomerDetails(customerId: string, body: {
  firstName: string;
  lastName: string;
  phone: string;
  companyName: string;
}): Promise<{ customer: Customer; changed: boolean; zohoUpdated: boolean; zohoWarning?: string | null }> {
  const raw = await client.patch<unknown>(`/api/customers/${encodeURIComponent(customerId)}/details`, body);
  return z.object({
    customer: CustomerSchema,
    changed: z.boolean(),
    zohoUpdated: z.boolean(),
    zohoWarning: z.string().nullish()
  }).parse(raw);
}

export interface CustomerNameReconciliation {
  projectsRenamed: number;
  intakesUpdated: number;
  pendingRecipientsUpdated: number;
}

export async function reconcileCustomerNames(customerId: string): Promise<CustomerNameReconciliation> {
  const raw = await client.post<unknown>(`/api/customers/${encodeURIComponent(customerId)}/reconcile-names`, {});
  return z.object({
    nameChanges: z.object({
      projectsRenamed: z.number(),
      intakesUpdated: z.number(),
      pendingRecipientsUpdated: z.number()
    })
  }).parse(raw).nameChanges;
}

export async function updateCustomerEmail(customerId: string, email: string): Promise<{
  customer: Customer;
  oldEmail?: string;
  emailChanged?: boolean;
  zohoUpdated?: boolean;
  zohoWarning?: string | null;
  pendingApprovalsUpdated?: number;
}> {
  const raw = await client.patch<unknown>(`/api/customers/${encodeURIComponent(customerId)}`, { email });
  return z.object({
    customer: CustomerSchema,
    oldEmail: z.string().optional(),
    emailChanged: z.boolean().optional(),
    zohoUpdated: z.boolean().optional(),
    zohoWarning: z.string().nullish(),
    pendingApprovalsUpdated: z.number().optional()
  }).passthrough().parse(raw);
}

export const CustomerBillingAddressSchema = z.object({
  address_line: nullableString, city: nullableString, state: nullableString, zip: nullableString, country: nullableString
});
export const CustomerAddressRepairSchema = z.object({
  status: z.enum(['ready', 'review', 'error']), source: z.string().optional(),
  reason: z.string().nullish(), address: CustomerBillingAddressSchema.nullable(),
  applied: z.boolean(), addressVerified: z.boolean()
});
export type CustomerAddressRepair = z.infer<typeof CustomerAddressRepairSchema>;

export async function repairCustomerAddress(customerId: string, apply = false): Promise<CustomerAddressRepair> {
  const raw = await client.post<unknown>(`/api/customers/${encodeURIComponent(customerId)}/address-repair`, { apply });
  return CustomerAddressRepairSchema.parse(raw);
}

export async function updateCustomerAddress(customerId: string, body: {
  addressLine: string; city: string; state: string; zip: string; country: string;
}): Promise<{ customer: Customer; zohoWarning?: string | null }> {
  const raw = await client.patch<unknown>(`/api/customers/${encodeURIComponent(customerId)}/address`, body);
  return z.object({ customer: CustomerSchema, zohoWarning: z.string().nullish() }).parse(raw);
}

export async function syncCustomerToZoho(customerId: string): Promise<{ zohoCustomerId?: string; alreadySynced?: boolean; zohoContactName?: string }> {
  const raw = await client.post<unknown>(`/api/customers/${encodeURIComponent(customerId)}/sync-zoho`, {});
  return z.object({
    zohoCustomerId: z.string().optional(),
    alreadySynced: z.boolean().optional(),
    zohoContactName: z.string().optional()
  }).passthrough().parse(raw);
}

export async function archiveCustomer(customerId: string): Promise<Customer> {
  const raw = await client.post<unknown>(`/api/customers/${encodeURIComponent(customerId)}/archive`, {});
  return z.object({ customer: CustomerSchema }).parse(raw).customer;
}

export async function restoreCustomer(customerId: string): Promise<Customer> {
  const raw = await client.post<unknown>(`/api/customers/${encodeURIComponent(customerId)}/unarchive`, {});
  return z.object({ customer: CustomerSchema }).parse(raw).customer;
}

export async function permanentlyDeleteCustomer(customerId: string, confirmName: string): Promise<unknown> {
  return client.delete<unknown>(`/api/customers/${encodeURIComponent(customerId)}`, { confirmName });
}

export async function createCustomerProject(body: {
  customerId: string;
  name: string;
  stage: 'Active' | 'Pending' | 'Completed' | 'Archived';
}): Promise<Project> {
  const raw = await client.post<unknown>('/api/customer-projects', body);
  return z.object({ project: ProjectSchema }).parse(raw).project;
}

export async function getProjects(includeArchived = false): Promise<Project[]> {
  const query = includeArchived ? '?includeArchived=1' : '';
  const raw = await client.get<unknown>(`/api/projects${query}`);
  return ProjectsResponseSchema.parse(raw).projects;
}

export async function getProject(projectId: string): Promise<ProjectDetail> {
  const raw = await client.get<unknown>(`/api/projects/${encodeURIComponent(projectId)}`);
  return ProjectDetailResponseSchema.parse(raw);
}

export async function markSubstantialCompletion(projectId: string): Promise<{ project: Project; milestone: Milestone; invoice?: unknown; phaseEmail?: unknown }> {
  const raw = await client.post<unknown>(`/api/projects/${encodeURIComponent(projectId)}/substantial-completion`, {});
  return z.object({
    project: ProjectSchema,
    milestone: MilestoneSchema,
    invoice: z.unknown().optional(),
    phaseEmail: z.unknown().optional()
  }).passthrough().parse(raw);
}

export async function markProjectCompletion(projectId: string): Promise<{ project: Project; milestone: Milestone; invoice?: unknown; phaseEmail?: unknown }> {
  const raw = await client.post<unknown>(`/api/projects/${encodeURIComponent(projectId)}/project-completion`, {});
  return z.object({
    project: ProjectSchema,
    milestone: MilestoneSchema,
    invoice: z.unknown().optional(),
    phaseEmail: z.unknown().optional()
  }).passthrough().parse(raw);
}

export async function getProjectCloseout(projectId: string): Promise<CloseoutState> {
  const raw = await client.get<unknown>(`/api/projects/${encodeURIComponent(projectId)}/closeout`);
  return CloseoutStateSchema.parse(raw);
}

export async function updateCloseoutItem(itemId: string, input: { status?: 'Pending' | 'Yes' | 'No' | 'N/A'; notes?: string | null }): Promise<{ item: CloseoutItem; readiness: CloseoutReadiness | null }> {
  const raw = await client.patch<unknown>(`/api/closeout-items/${encodeURIComponent(itemId)}`, input);
  const parsed = z.object({ item: CloseoutItemSchema, readiness: CloseoutReadinessSchema.nullish() }).parse(raw);
  return { item: parsed.item, readiness: parsed.readiness ?? null };
}

export async function updateProjectCloseout(projectId: string, input: {
  finalAuditStatus?: 'Pending' | 'In Review' | 'Completed';
  paymentVerified?: boolean;
  documentsComplete?: boolean;
  customerSatisfactionComplete?: boolean;
  warrantyCreated?: boolean;
  completedBy?: string | null;
}): Promise<CloseoutState> {
  const raw = await client.patch<unknown>(`/api/projects/${encodeURIComponent(projectId)}/closeout`, input);
  return CloseoutStateSchema.parse(raw);
}

export async function getInvoices(): Promise<Invoice[]> {
  const raw = await client.get<unknown>('/api/invoices');
  return z.object({ invoices: z.array(InvoiceSchema).default([]) }).parse(raw).invoices;
}

export async function stageProjectInvoice(input: {
  projectId: string;
  amount: number;
  description?: string | null;
  zohoProjectId?: string | null;
}): Promise<{ invoice: Invoice; paymentUrl?: string | null; zohoSyncError?: string | null }> {
  const raw = await client.post<unknown>('/api/invoice/stage', input);
  return z.object({
    invoice: InvoiceSchema,
    paymentUrl: nullableString,
    zohoSyncError: nullableString
  }).passthrough().parse(raw);
}

export async function getMilestones(projectId?: string): Promise<Milestone[]> {
  const query = projectId ? `?projectId=${encodeURIComponent(projectId)}` : '';
  const raw = await client.get<unknown>(`/api/milestones${query}`);
  return z.object({ milestones: z.array(MilestoneSchema).default([]) }).parse(raw).milestones;
}

export async function createMilestone(input: {
  projectId: string;
  title: string;
  percentComplete?: number;
  amountDue?: number;
}): Promise<Milestone> {
  const raw = await client.post<unknown>('/api/milestones', input);
  return z.object({ milestone: MilestoneSchema }).parse(raw).milestone;
}

export async function updateMilestone(milestoneId: string, input: {
  title?: string;
  percentComplete?: number;
  amountDue?: number;
}): Promise<Milestone> {
  const raw = await client.patch<unknown>(`/api/milestones/${encodeURIComponent(milestoneId)}`, input);
  return z.object({ milestone: MilestoneSchema }).parse(raw).milestone;
}

export async function deleteMilestone(milestoneId: string): Promise<unknown> {
  return client.delete<unknown>(`/api/milestones/${encodeURIComponent(milestoneId)}`);
}

export async function createMilestoneInvoice(milestoneId: string): Promise<{ milestone: Milestone; alreadyInvoiced?: boolean; zohoSyncError?: string | null }> {
  const raw = await client.post<unknown>(`/api/milestones/${encodeURIComponent(milestoneId)}/invoice`, {});
  return z.object({ milestone: MilestoneSchema, alreadyInvoiced: z.boolean().optional(), zohoSyncError: nullableString }).passthrough().parse(raw);
}

export async function createMilestoneRetainer(milestoneId: string): Promise<{ milestone: Milestone; alreadyInvoiced?: boolean; zohoSyncError?: string | null }> {
  const raw = await client.post<unknown>(`/api/milestones/${encodeURIComponent(milestoneId)}/retainer`, {});
  return z.object({ milestone: MilestoneSchema, alreadyInvoiced: z.boolean().optional(), zohoSyncError: nullableString }).passthrough().parse(raw);
}

export async function getPaymentElection(eventType: 'milestone' | 'change-order', eventId: string): Promise<{ currentElection: PaymentElection | null; elections: PaymentElection[] }> {
  const raw = await client.get<unknown>(`/api/payment-elections/${eventType}/${encodeURIComponent(eventId)}`);
  return z.object({
    currentElection: PaymentElectionSchema.nullish().transform((value) => value || null),
    elections: z.array(PaymentElectionSchema).default([])
  }).parse(raw);
}

export async function recordPaymentElection(eventType: 'milestone' | 'change-order', eventId: string, input: {
  methodCode: 'ach' | 'card' | 'check' | 'wire' | 'cash' | 'other';
  methodLabel?: string | null;
  electedByRole?: string;
  recordedByEmail?: string | null;
  notes?: string | null;
}): Promise<PaymentElection> {
  const raw = await client.post<unknown>(`/api/payment-elections/${eventType}/${encodeURIComponent(eventId)}`, input);
  return z.object({ election: PaymentElectionSchema }).parse(raw).election;
}

export async function sendRelayPaymentLink(
  eventType: 'milestone' | 'change-order',
  eventId: string,
  paymentUrl: string
): Promise<{
  status: string;
  paymentUrl: string;
  email?: { sent?: boolean; error?: string | null };
}> {
  return client.post(`/api/relay-payments/${eventType}/${encodeURIComponent(eventId)}/send`, {
    paymentUrl
  });
}

export async function confirmRelayPayment(
  eventType: 'milestone' | 'change-order',
  eventId: string,
  input: {
    receivedDate: string;
    feeAmount?: number;
  }
): Promise<{
  status: string;
  payment: Record<string, unknown>;
}> {
  return client.post(`/api/relay-payments/${eventType}/${encodeURIComponent(eventId)}/confirm`, input);
}
export async function getProjectFinancialSummary(projectId: string): Promise<FinancialSummary | null> {
  const raw = await client.get<unknown>(`/api/projects/${encodeURIComponent(projectId)}/financial-summary`);
  return z.object({ financialSummary: FinancialSummarySchema.nullish() }).parse(raw).financialSummary || null;
}

async function downloadBlob(path: string, init?: RequestInit): Promise<Blob> {
  const response = await fetch(`${DEFAULT_API_BASE_URL}${path}`, { credentials: 'include', ...init });
  if (!response.ok) {
    const payload = await response.json().catch(() => null) as { error?: string | { message?: string } } | null;
    const error = typeof payload?.error === 'string' ? payload.error : payload?.error?.message;
    throw new ApiError(error || `Request failed (${response.status})`, response.status, payload);
  }
  return response.blob();
}

export function staffFinancialStatementBlob(projectId: string, filter: 'all' | 'invoices' | 'payments' | 'change-orders' = 'all'): Promise<Blob> {
  return downloadBlob(`/api/projects/${encodeURIComponent(projectId)}/financial-statement`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/pdf' },
    body: JSON.stringify({ filter })
  });
}

export async function getEstimates(): Promise<Estimate[]> {
  const raw = await client.get<unknown>('/api/estimates');
  return z.object({ estimates: z.array(EstimateSchema).default([]) }).parse(raw).estimates;
}

export async function getEstimate(estimateId: string): Promise<EstimateDetail> {
  const raw = await client.get<unknown>(`/api/estimates/${encodeURIComponent(estimateId)}`);
  return EstimateDetailResponseSchema.parse(raw);
}

export async function createEstimate(input: {
  projectId: string;
  title?: string | null;
  scopeOfWork?: string | null;
  notes?: string | null;
  total?: number;
}): Promise<Estimate> {
  const raw = await client.post<unknown>('/api/estimates', input);
  return z.object({ estimate: EstimateSchema }).parse(raw).estimate;
}

export async function updateEstimateDetails(estimateId: string, input: {
  title?: string | null;
  scopeOfWork?: string | null;
  notes?: string | null;
}): Promise<{ estimate: Estimate; changed: boolean; version?: number }> {
  const raw = await client.patch<unknown>(`/api/estimates/${encodeURIComponent(estimateId)}/details`, input);
  return z.object({
    estimate: EstimateSchema,
    changed: z.boolean().default(false),
    version: z.number().optional()
  }).passthrough().parse(raw);
}

export async function updateEstimateStatus(estimateId: string, status: string): Promise<Estimate> {
  const raw = await client.patch<unknown>(`/api/estimates/${encodeURIComponent(estimateId)}`, { status });
  return z.object({ estimate: EstimateSchema }).parse(raw).estimate;
}

export async function deleteEstimate(estimateId: string): Promise<unknown> {
  return client.delete<unknown>(`/api/estimates/${encodeURIComponent(estimateId)}`);
}

export async function consolidateEstimates(projectId: string, estimateIds: string[]): Promise<Record<string, unknown>> {
  return client.post<Record<string, unknown>>(`/api/projects/${encodeURIComponent(projectId)}/estimates/consolidate`, { estimateIds });
}

export async function createEstimateLine(input: {
  estimateId: string;
  description?: string;
  quantity: number;
  rate?: number;
  usageUnit?: string | null;
  accountCode?: number | null;
  zohoItemId?: string | null;
  assignedRole?: string | null;
  requiresSelection?: boolean;
  priceBookItemId?: string | null;
  priceBookTier?: 'Basic' | 'Standard' | 'Premium' | null;
}): Promise<Record<string, unknown>> {
  return client.post<Record<string, unknown>>('/api/estimates/line-items', input);
}

export async function updateEstimateLinesBatch(estimateId: string, baseVersion: number, lines: Array<{
  id: string;
  description: string;
  quantity: number;
  rate: number;
  usageUnit?: string | null;
  accountCode: number;
  zohoItemId?: string | null;
  requiresSelection?: boolean;
}>): Promise<{ status?: string; changed?: number; version?: number; total?: number }> {
  const raw = await client.patch<unknown>(`/api/estimates/${encodeURIComponent(estimateId)}/line-items/batch`, { baseVersion, lines });
  return z.object({
    status: z.string().optional(),
    changed: z.number().optional(),
    version: z.number().optional(),
    total: z.number().optional()
  }).passthrough().parse(raw);
}

export async function deleteEstimateLine(lineId: string): Promise<Record<string, unknown>> {
  return client.delete<Record<string, unknown>>(`/api/estimates/line-items/${encodeURIComponent(lineId)}`);
}

export async function getEstimateConversionGate(estimateId: string): Promise<ConversionGate> {
  const raw = await client.get<unknown>(`/api/estimates/${encodeURIComponent(estimateId)}/conversion-gate`);
  return ConversionGateSchema.parse(raw);
}

export async function submitEstimateApproval(estimateId: string, customerEmail: string): Promise<{
  approvalId: string;
  lineItemApprovals?: number;
  emailSent?: boolean;
  emailError?: string | null;
}> {
  const raw = await client.post<unknown>('/api/estimates/submit-approval', { estimateId, customerEmail });
  return z.object({
    approvalId: z.string(),
    lineItemApprovals: z.number().optional(),
    emailSent: z.boolean().optional(),
    emailError: z.string().nullish()
  }).passthrough().parse(raw);
}

export async function markEstimateReady(estimateId: string): Promise<{ ready: boolean; failedChecks?: Array<Record<string, unknown>> }> {
  const raw = await client.post<unknown>('/api/estimates/mark-ready', { estimateId });
  return z.object({
    ready: z.boolean(),
    failedChecks: z.array(z.record(z.string(), z.unknown())).optional()
  }).passthrough().parse(raw);
}

export async function finalEstimateApproval(estimateId: string): Promise<Record<string, unknown>> {
  return client.post<Record<string, unknown>>('/api/estimates/final-approval', { estimateId });
}

export async function getPriceBook(includeInactive = false): Promise<PriceBook> {
  const query = includeInactive ? '?includeInactive=1' : '';
  const raw = await client.get<unknown>(`/api/price-book${query}`);
  return PriceBookSchema.parse(raw);
}

export async function getZohoItems(): Promise<ZohoItem[]> {
  const raw = await client.get<unknown>('/api/zoho/items');
  return z.object({ items: z.array(ZohoItemSchema).default([]) }).parse(raw).items;
}

export async function getProjectContractGate(projectId: string): Promise<ContractConversionGate> {
  const raw = await client.get<unknown>(`/api/projects/${encodeURIComponent(projectId)}/conversion-gate`);
  return ContractConversionGateSchema.parse(raw);
}

export async function createProjectContract(projectId: string, input: {
  title?: string;
  estimatedStartDate: string;
  estimatedCompletionDate: string;
}): Promise<Contract> {
  const raw = await client.post<unknown>(`/api/projects/${encodeURIComponent(projectId)}/contracts`, input);
  return z.object({ contract: ContractSchema }).parse(raw).contract;
}

export async function regenerateContract(contractId: string, input: {
  title?: string;
  estimatedStartDate: string;
  estimatedCompletionDate: string;
}): Promise<Contract> {
  const raw = await client.post<unknown>(`/api/contracts/${encodeURIComponent(contractId)}/regenerate`, input);
  return z.object({ contract: ContractSchema }).passthrough().parse(raw).contract;
}

export async function sendContractForSignature(contractId: string, input: {
  documentId: string;
  recipientName?: string | null;
  recipientEmail?: string | null;
}): Promise<Contract[]> {
  const raw = await client.post<unknown>(`/api/staff/contracts/${encodeURIComponent(contractId)}/send-for-signature`, input);
  return z.object({ contracts: z.array(ContractSchema).default([]) }).passthrough().parse(raw).contracts;
}

export async function staffSignContract(contractId: string, input: {
  signerName: string;
  signerEmail: string;
  consentAccepted: true;
  signatureMethod: 'typed' | 'drawn';
  signatureData: string;
}): Promise<Contract[]> {
  const raw = await client.post<unknown>(`/api/staff/contracts/${encodeURIComponent(contractId)}/sign`, input);
  return z.object({ contracts: z.array(ContractSchema).default([]) }).parse(raw).contracts;
}

export async function executeUploadedContract(contractId: string, input: {
  documentId: string;
  executionDate: string;
  confirmedBothSigned: true;
  executedBy: string;
  executedByEmail: string;
}): Promise<Contract[]> {
  const raw = await client.post<unknown>(`/api/staff/contracts/${encodeURIComponent(contractId)}/execute-uploaded`, input);
  return z.object({ contracts: z.array(ContractSchema).default([]) }).parse(raw).contracts;
}

export async function portalReviewContract(contractId: string, email: string, token?: string): Promise<{
  documentId: string;
  sha256: string;
  consentText?: string | null;
  consentVersion?: string | null;
  projectDisclosureAcknowledged?: boolean;
  projectDisclosureText?: string | null;
  projectDisclosureVersion?: string | null;
}> {
  const raw = await client.post<unknown>(`/api/portal/contracts/${encodeURIComponent(contractId)}/view`, { email, token: token || undefined });
  return z.object({
    documentId: z.string(),
    sha256: z.string(),
    consentText: nullableString,
    consentVersion: nullableString,
    projectDisclosureAcknowledged: z.boolean().optional(),
    projectDisclosureText: nullableString,
    projectDisclosureVersion: nullableString
  }).passthrough().parse(raw);
}

export async function portalContractDocumentBlob(contractId: string, email: string, token?: string): Promise<Blob> {
  const response = await fetch(`${DEFAULT_API_BASE_URL}/api/portal/contracts/${encodeURIComponent(contractId)}/document`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', Accept: 'application/pdf' },
    body: JSON.stringify({ email, token: token || undefined })
  });
  if (!response.ok) {
    const payload = await response.json().catch(() => null) as Record<string, unknown> | null;
    const nested = payload?.error && typeof payload.error === 'object' ? payload.error as Record<string, unknown> : null;
    throw new ApiError(String(nested?.message || payload?.error || `Request failed (${response.status})`), response.status, payload);
  }
  return response.blob();
}

export async function portalExecutedContractBlob(contractId: string, email: string): Promise<Blob> {
  const response = await fetch(`${DEFAULT_API_BASE_URL}/api/portal/contracts/${encodeURIComponent(contractId)}/executed-document?email=${encodeURIComponent(email)}`, {
    credentials: 'include',
    headers: { Accept: 'application/pdf' }
  });
  if (!response.ok) {
    const payload = await response.json().catch(() => null) as Record<string, unknown> | null;
    const nested = payload?.error && typeof payload.error === 'object' ? payload.error as Record<string, unknown> : null;
    throw new ApiError(String(nested?.message || payload?.error || `Request failed (${response.status})`), response.status, payload);
  }
  return response.blob();
}

export async function portalSignContract(contractId: string, input: {
  email: string;
  signerName: string;
  consentAccepted: true;
  projectDisclosureAccepted: true;
  signatureMethod: 'typed' | 'drawn';
  signatureData: string;
  token?: string;
}): Promise<Contract[]> {
  const raw = await client.post<unknown>(`/api/portal/contracts/${encodeURIComponent(contractId)}/sign`, input);
  return z.object({ contracts: z.array(ContractSchema).default([]) }).parse(raw).contracts;
}


export async function getChangeOrders(projectId: string): Promise<ChangeOrder[]> {
  const raw = await client.get<unknown>(`/api/change-orders?projectId=${encodeURIComponent(projectId)}`);
  return ChangeOrdersResponseSchema.parse(raw).changeOrders;
}

export async function getChangeOrder(changeOrderId: string): Promise<ChangeOrderDetail> {
  const raw = await client.get<unknown>(`/api/change-orders/${encodeURIComponent(changeOrderId)}`);
  return ChangeOrderDetailSchema.parse(raw);
}

export interface ChangeOrderLineInput {
  description: string;
  quantity: number;
  unitRate: number;
  estimatedCost?: number;
  requiresSelection?: boolean;
  options?: Array<{
    description: string;
    productCode?: string | null;
    productUrl?: string | null;
    price?: number;
  }>;
}

export async function createChangeOrder(input: {
  projectId: string;
  title: string;
  description?: string | null;
  scheduleImpactType?: 'none' | 'days' | 'tbd';
  scheduleImpactDays?: number;
  lines: ChangeOrderLineInput[];
}): Promise<ChangeOrderDetail> {
  const raw = await client.post<unknown>('/api/change-orders', input);
  return ChangeOrderDetailSchema.parse(raw);
}

export async function updateChangeOrder(changeOrderId: string, input: {
  title?: string;
  description?: string | null;
  scheduleImpactType?: 'none' | 'days' | 'tbd';
  scheduleImpactDays?: number;
  lines?: ChangeOrderLineInput[];
}): Promise<ChangeOrderDetail> {
  const raw = await client.patch<unknown>(`/api/change-orders/${encodeURIComponent(changeOrderId)}`, input);
  return ChangeOrderDetailSchema.parse(raw);
}

export async function selectChangeOrderLine(lineId: string, input: {
  chosenOptionId?: string | null;
  customerProductDescription?: string | null;
  customerProductCode?: string | null;
  customerProductUrl?: string | null;
  verifiedPrice?: number | null;
  selectedBy?: string;
}): Promise<ChangeOrderDetail> {
  const raw = await client.patch<unknown>(`/api/change-order-lines/${encodeURIComponent(lineId)}/selection`, input);
  return ChangeOrderDetailSchema.parse(raw);
}

export async function generateChangeOrder(changeOrderId: string): Promise<{
  document: ProjectDocument;
  detail: ChangeOrderDetail;
}> {
  const raw = await client.post<unknown>(`/api/staff/change-orders/${encodeURIComponent(changeOrderId)}/generate`, {});
  return z.object({ document: DocumentSchema, detail: ChangeOrderDetailSchema }).parse(raw);
}

export async function changeOrderPreviewBlob(changeOrderId: string, documentId: string): Promise<Blob> {
  const response = await fetch(`${DEFAULT_API_BASE_URL}/api/staff/change-orders/${encodeURIComponent(changeOrderId)}/preview?documentId=${encodeURIComponent(documentId)}`, {
    credentials: 'include',
    headers: { Accept: 'application/pdf' }
  });
  if (!response.ok) {
    const payload = await response.json().catch(() => null) as Record<string, unknown> | null;
    const nested = payload?.error && typeof payload.error === 'object' ? payload.error as Record<string, unknown> : null;
    throw new ApiError(String(nested?.message || payload?.error || `Request failed (${response.status})`), response.status, payload);
  }
  return response.blob();
}

export async function sendChangeOrderForSignature(changeOrderId: string, documentId: string): Promise<ChangeOrderDetail> {
  const raw = await client.post<unknown>(`/api/staff/change-orders/${encodeURIComponent(changeOrderId)}/send-for-signature`, { documentId });
  return z.object({ detail: ChangeOrderDetailSchema }).passthrough().parse(raw).detail;
}

export async function staffSignChangeOrder(changeOrderId: string, input: {
  signerName: string;
  signerEmail: string;
  consentAccepted: true;
  signatureMethod: 'typed' | 'drawn';
  signatureData: string;
}): Promise<{ detail: ChangeOrderDetail; zohoSyncError?: string | null }> {
  const raw = await client.post<unknown>(`/api/staff/change-orders/${encodeURIComponent(changeOrderId)}/sign`, input);
  return z.object({ detail: ChangeOrderDetailSchema, zohoSyncError: nullableString }).passthrough().parse(raw);
}

export async function createChangeOrderInvoice(changeOrderId: string): Promise<ChangeOrderDetail> {
  const raw = await client.post<unknown>(`/api/staff/change-orders/${encodeURIComponent(changeOrderId)}/create-invoice`, {});
  return z.object({ detail: ChangeOrderDetailSchema }).passthrough().parse(raw).detail;
}

export async function portalChangeOrderDocumentBlob(changeOrderId: string, email: string): Promise<Blob> {
  const response = await fetch(`${DEFAULT_API_BASE_URL}/api/portal/change-orders/${encodeURIComponent(changeOrderId)}/document`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', Accept: 'application/pdf' },
    body: JSON.stringify({ email })
  });
  if (!response.ok) {
    const payload = await response.json().catch(() => null) as Record<string, unknown> | null;
    const nested = payload?.error && typeof payload.error === 'object' ? payload.error as Record<string, unknown> : null;
    throw new ApiError(String(nested?.message || payload?.error || `Request failed (${response.status})`), response.status, payload);
  }
  return response.blob();
}

export async function portalExecutedChangeOrderBlob(changeOrderId: string, email: string): Promise<Blob> {
  const response = await fetch(`${DEFAULT_API_BASE_URL}/api/portal/change-orders/${encodeURIComponent(changeOrderId)}/executed-document?email=${encodeURIComponent(email)}`, {
    credentials: 'include',
    headers: { Accept: 'application/pdf' }
  });
  if (!response.ok) {
    const payload = await response.json().catch(() => null) as Record<string, unknown> | null;
    const nested = payload?.error && typeof payload.error === 'object' ? payload.error as Record<string, unknown> : null;
    throw new ApiError(String(nested?.message || payload?.error || `Request failed (${response.status})`), response.status, payload);
  }
  return response.blob();
}

export async function portalSignChangeOrder(changeOrderId: string, input: {
  email: string;
  signerName: string;
  consentAccepted: true;
  signatureMethod: 'typed' | 'drawn';
  signatureData: string;
}): Promise<ChangeOrder[]> {
  const raw = await client.post<unknown>(`/api/portal/change-orders/${encodeURIComponent(changeOrderId)}/sign`, input);
  return ChangeOrdersResponseSchema.parse(raw).changeOrders;
}

export const NC_PROJECT_COUNTIES = ['Henderson', 'Buncombe', 'Transylvania', 'Polk', 'Rutherford', 'Haywood'] as const;

export async function updateProject(
  projectId: string,
  body: {
    name?: string;
    stage?: string;
    ncTaxTreatment?: string;
    jobsiteCounty?: string | null;
    e589ciStatus?: string;
    e589ciDocumentId?: string | null;
    e589ciExceptionReason?: string | null;
    contractPrice?: number;
  }
): Promise<Project> {
  const raw = await client.patch<unknown>(`/api/projects/${encodeURIComponent(projectId)}`, body);
  return z.object({ project: ProjectSchema }).parse(raw).project;
}

export const E589ciAffidavitSchema = z.object({
  id: z.string(),
  project_id: z.string(),
  contract_id: nullableString,
  customer_id: z.string(),
  status: z.string(),
  form_version: nullableString,
  owner_name: nullableString,
  owner_address: nullableString,
  owner_city: nullableString,
  owner_state: nullableString,
  owner_zip: nullableString,
  contractor_name: nullableString,
  improvement_description: nullableString,
  project_name: nullableString,
  project_address: nullableString,
  project_city: nullableString,
  project_state: nullableString,
  project_zip: nullableString,
  prepared_document_id: nullableString,
  prepared_sha256: nullableString,
  signed_document_id: nullableString,
  consent_text: nullableString,
  consent_version: nullableString,
  signer_name: nullableString,
  signer_title: nullableString,
  signer_email: nullableString,
  sent_at: nullableString,
  signed_at: nullableString,
  created_at: nullableString,
  updated_at: nullableString
}).passthrough();
export type E589ciAffidavit = z.infer<typeof E589ciAffidavitSchema>;

const E589ciResponseSchema = z.object({
  affidavit: E589ciAffidavitSchema.nullish(),
  autofill: z.record(z.string(), z.unknown()).nullish()
}).passthrough();

export async function getProjectE589ci(projectId: string) {
  const raw = await client.get<unknown>(`/api/projects/${encodeURIComponent(projectId)}/e589ci`);
  return E589ciResponseSchema.parse(raw);
}

export async function prepareProjectE589ci(projectId: string, fields?: Record<string, string | null>) {
  const raw = await client.post<unknown>(`/api/projects/${encodeURIComponent(projectId)}/e589ci/prepare`, { fields: fields || {} });
  return E589ciResponseSchema.parse(raw);
}

export async function sendProjectE589ciForSignature(projectId: string) {
  const raw = await client.post<unknown>(`/api/projects/${encodeURIComponent(projectId)}/e589ci/send-for-signature`, {});
  return E589ciResponseSchema.extend({
    emailSent: z.boolean().optional(),
    emailError: nullableString
  }).parse(raw);
}

export async function portalGetE589ci(projectId: string, email: string): Promise<E589ciAffidavit | null> {
  const raw = await client.get<unknown>(`/api/portal/projects/${encodeURIComponent(projectId)}/e589ci?email=${encodeURIComponent(email)}`);
  return E589ciResponseSchema.parse(raw).affidavit || null;
}

export async function portalSignE589ci(projectId: string, input: {
  email: string;
  signerName: string;
  signerTitle?: string;
  consentAccepted: true;
  signatureMethod: 'typed' | 'drawn';
  signatureData: string;
}): Promise<E589ciAffidavit> {
  const raw = await client.post<unknown>(`/api/portal/projects/${encodeURIComponent(projectId)}/e589ci/sign`, input);
  const affidavit = E589ciResponseSchema.parse(raw).affidavit;
  if (!affidavit) throw new Error('Signed E-589CI response was incomplete.');
  return affidavit;
}

export async function getProjectManageData(projectId: string): Promise<ProjectManageData> {
  const raw = await client.get<unknown>(`/api/projects/${encodeURIComponent(projectId)}/manage-data`);
  return ProjectManageDataSchema.parse(raw);
}

export async function updateProjectCustomer(projectId: string, customerId: string | null): Promise<Project> {
  const raw = await client.patch<unknown>(`/api/projects/${encodeURIComponent(projectId)}/customer`, { customerId });
  return z.object({ project: ProjectSchema }).parse(raw).project;
}

export async function clearProjectTasks(projectId: string): Promise<{ deletedTasks: number }> {
  const raw = await client.post<unknown>(`/api/projects/${encodeURIComponent(projectId)}/clear-tasks`, { confirm: 'CLEAR TASKS' });
  return z.object({ deletedTasks: z.number().default(0) }).passthrough().parse(raw);
}

export async function clearProjectSelections(projectId: string): Promise<{ deletedSelections: number; deletedCustomerSelections: number }> {
  const raw = await client.post<unknown>(`/api/projects/${encodeURIComponent(projectId)}/clear-selections`, { confirm: 'CLEAR SELECTIONS' });
  return z.object({
    deletedSelections: z.number().default(0),
    deletedCustomerSelections: z.number().default(0)
  }).passthrough().parse(raw);
}

export async function archiveProject(projectId: string, reason?: string | null): Promise<Project> {
  const raw = await client.post<unknown>(`/api/projects/${encodeURIComponent(projectId)}/archive`, { reason: reason || null });
  return z.object({ project: ProjectSchema }).parse(raw).project;
}

export async function restoreProject(projectId: string): Promise<Project> {
  const raw = await client.post<unknown>(`/api/projects/${encodeURIComponent(projectId)}/unarchive`, {});
  return z.object({ project: ProjectSchema }).parse(raw).project;
}

export async function permanentlyDeleteProject(projectId: string, confirmName: string): Promise<unknown> {
  return client.delete<unknown>(`/api/projects/${encodeURIComponent(projectId)}`, { confirmName });
}

export async function getTasks(): Promise<ProjectTask[]> {
  const raw = await client.get<unknown>('/api/tasks');
  return z.object({ tasks: z.array(TaskSchema).default([]) }).parse(raw).tasks;
}

export async function createTask(input: {
  projectId: string;
  title: string;
  status?: 'To Do' | 'In Progress' | 'Done';
  assignedTo?: 'Altios' | 'Customer' | 'Both' | 'Subcontractor';
  dueDate?: string | null;
  priority?: number;
  phase?: 'Preconstruction' | 'Project Build' | 'Substantial Completion' | 'Closeout';
  taskType?: 'Task' | 'Milestone';
  workPhase?: string | null;
  startDate?: string | null;
  endDate?: string | null;
  showOnTimeline?: boolean;
  dependencies?: string[];
  details?: string | null;
  customerTitle?: string | null;
  customerInstructions?: string | null;
}): Promise<ProjectTask> {
  const raw = await client.post<unknown>('/api/tasks', input);
  return z.object({ task: TaskSchema }).parse(raw).task;
}

export async function updateTask(taskId: string, input: Record<string, unknown>): Promise<ProjectTask> {
  const raw = await client.patch<unknown>(`/api/tasks/${encodeURIComponent(taskId)}`, input);
  return z.object({ task: TaskSchema }).parse(raw).task;
}

export async function updateTaskPhaseProgress(taskId: string, approved: boolean): Promise<ProjectTask> {
  const raw = await client.patch<unknown>(`/api/tasks/${encodeURIComponent(taskId)}/phase-progress`, { approved });
  return z.object({ task: TaskSchema }).parse(raw).task;
}

export async function deleteTask(taskId: string): Promise<unknown> {
  return client.delete<unknown>(`/api/tasks/${encodeURIComponent(taskId)}`);
}

export interface AiPlanTask {
  key: string;
  title: string;
  taskType: 'Task' | 'Milestone';
  workPhase: string;
  assignedTo: 'Altios' | 'Customer' | 'Both' | 'Subcontractor';
  priority: number;
  startDate: string | null;
  endDate: string | null;
  showOnTimeline: boolean;
  dependencies: string[];
  details: string;
  customerTitle: string;
  customerInstructions: string;
}

export interface AiProjectPlan {
  runId: string;
  summary?: string;
  tasks: AiPlanTask[];
  sourceContractId?: string;
  sourceEstimateId?: string;
  sourceEstimateVersionId?: string;
  generatedAt?: string;
}

export async function generateAiProjectPlan(projectId: string): Promise<AiProjectPlan> {
  const raw = await client.post<{ plan: AiProjectPlan }>(`/api/projects/${encodeURIComponent(projectId)}/plan/ai`, {});
  return raw.plan;
}

export async function getLatestAiProjectPlan(projectId: string): Promise<AiProjectPlan | null> {
  const raw = await client.get<{ plan: AiProjectPlan | null }>(`/api/projects/${encodeURIComponent(projectId)}/plan/ai/latest`);
  return raw.plan || null;
}

export async function applyAiProjectPlan(projectId: string, runId: string, tasks: AiPlanTask[]): Promise<{ created: number; tasks: ProjectTask[] }> {
  const raw = await client.post<unknown>(`/api/projects/${encodeURIComponent(projectId)}/plan/apply`, { runId, tasks });
  return z.object({ created: z.number().default(0), tasks: z.array(TaskSchema).default([]) }).parse(raw);
}

export async function getProjectCalendar(projectId: string): Promise<ProjectCalendar> {
  const raw = await client.get<unknown>(`/api/projects/${encodeURIComponent(projectId)}/calendar`);
  return ProjectCalendarSchema.parse(raw);
}

export async function createAppointment(input: {
  projectId: string;
  appointmentType: string;
  title: string;
  startsAt: string;
  endsAt: string;
  timezone?: string;
  location?: string | null;
  customerNotes?: string | null;
  internalNotes?: string | null;
  customerVisible?: boolean;
}): Promise<Appointment> {
  const raw = await client.post<unknown>('/api/appointments', input);
  return z.object({ appointment: AppointmentSchema }).parse(raw).appointment;
}

export async function updateAppointment(appointmentId: string, input: Record<string, unknown>): Promise<Appointment> {
  const raw = await client.patch<unknown>(`/api/appointments/${encodeURIComponent(appointmentId)}`, input);
  return z.object({ appointment: AppointmentSchema }).parse(raw).appointment;
}

export async function createProjectUnavailability(input: {
  projectId: string;
  ownerType: 'Altios' | 'Customer';
  startsAt: string;
  endsAt: string;
  allDay?: boolean;
  note?: string | null;
}): Promise<ProjectUnavailability> {
  const raw = await client.post<unknown>('/api/unavailability', input);
  return z.object({ unavailability: ProjectUnavailabilitySchema }).parse(raw).unavailability;
}

export async function removeProjectUnavailability(id: string): Promise<ProjectUnavailability> {
  const raw = await client.patch<unknown>(`/api/unavailability/${encodeURIComponent(id)}/remove`, {});
  return z.object({ unavailability: ProjectUnavailabilitySchema }).parse(raw).unavailability;
}

export async function getDocuments(): Promise<ProjectDocument[]> {
  const raw = await client.get<unknown>('/api/documents');
  return DocumentsResponseSchema.parse(raw).documents;
}

export async function uploadDocuments(input: {
  category: string;
  projectId?: string | null;
  contractId?: string | null;
  files: File[];
  caption?: string;
  mediaStage?: '' | 'Before' | 'Progress' | 'After';
  locationLabel?: string;
  capturedAt?: string;
  is360?: boolean;
  customerVisible?: boolean;
}): Promise<ProjectDocument[]> {
  const body = new FormData();
  body.append('category', input.category);
  if (input.projectId) body.append('projectId', input.projectId);
  if (input.contractId) body.append('contractId', input.contractId);
  body.append('caption', input.caption || '');
  body.append('mediaStage', input.mediaStage || '');
  body.append('locationLabel', input.locationLabel || '');
  body.append('capturedAt', input.capturedAt || '');
  body.append('is360', input.is360 ? '1' : '0');
  body.append('customerVisible', input.customerVisible ? '1' : '0');
  input.files.forEach((file) => body.append('files', file));
  const raw = await client.postForm<unknown>('/api/documents/upload', body);
  return DocumentsResponseSchema.parse(raw).documents;
}

export async function updateDocumentMetadata(documentId: string, body: {
  category: string;
  caption?: string;
  mediaStage?: '' | 'Before' | 'Progress' | 'After';
  locationLabel?: string;
  capturedAt?: string;
  is360?: boolean;
  customerVisible?: boolean;
}): Promise<ProjectDocument> {
  const raw = await client.patch<unknown>(`/api/documents/${encodeURIComponent(documentId)}/metadata`, body);
  return z.object({ document: DocumentSchema }).parse(raw).document;
}

export async function archiveDocument(documentId: string): Promise<unknown> {
  return client.delete<unknown>(`/api/documents/${encodeURIComponent(documentId)}`);
}

export async function restoreDocument(documentId: string): Promise<unknown> {
  return client.post<unknown>(`/api/documents/${encodeURIComponent(documentId)}/restore`, {});
}

export async function getDesignPreviews(projectId: string): Promise<DesignPreview[]> {
  const raw = await client.get<unknown>(`/api/projects/${encodeURIComponent(projectId)}/design-previews`);
  return DesignPreviewsResponseSchema.parse(raw).previews;
}

export async function createDesignPreview(projectId: string, input: {
  mode: 'photo' | 'concept';
  prompt: string;
  title?: string;
  selectionId?: string | null;
  sourceDocumentId?: string | null;
  widthIn?: number | null;
  heightIn?: number | null;
  depthIn?: number | null;
  material?: string;
  finish?: string;
  referenceDocumentIds?: string[];
}): Promise<DesignPreview> {
  const raw = await client.post<unknown>(`/api/projects/${encodeURIComponent(projectId)}/design-previews`, input);
  return z.object({ preview: DesignPreviewSchema }).parse(raw).preview;
}


export async function refineDesignPreview(
  projectId: string,
  previewId: string,
  prompt: string,
  referenceDocumentIds: string[] = []
): Promise<DesignPreview> {
  const raw = await client.post<unknown>(
    `/api/projects/${encodeURIComponent(projectId)}/design-previews/${encodeURIComponent(previewId)}/refine`,
    { prompt, referenceDocumentIds }
  );
  return z.object({ preview: DesignPreviewSchema }).parse(raw).preview;
}

export async function getSelectionDesignReferences(
  projectId: string,
  selectionId: string
): Promise<SelectionDesignReference[]> {
  const raw = await client.get<unknown>(
    `/api/projects/${encodeURIComponent(projectId)}/selections/${encodeURIComponent(selectionId)}/design-references`
  );
  return z.object({
    references: z.array(SelectionDesignReferenceSchema).default([])
  }).parse(raw).references;
}

export async function attachSelectionDesignReference(
  projectId: string,
  selectionId: string,
  documentId: string,
  note?: string
): Promise<SelectionDesignReference[]> {
  const raw = await client.post<unknown>(
    `/api/projects/${encodeURIComponent(projectId)}/selections/${encodeURIComponent(selectionId)}/design-references`,
    { documentId, note }
  );
  return z.object({
    references: z.array(SelectionDesignReferenceSchema).default([])
  }).parse(raw).references;
}

export async function removeSelectionDesignReference(
  projectId: string,
  selectionId: string,
  documentId: string
): Promise<SelectionDesignReference[]> {
  const raw = await client.delete<unknown>(
    `/api/projects/${encodeURIComponent(projectId)}/selections/${encodeURIComponent(selectionId)}/design-references/${encodeURIComponent(documentId)}`
  );
  return z.object({
    references: z.array(SelectionDesignReferenceSchema).default([])
  }).parse(raw).references;
}

export async function updateDesignPreviewSharing(
  projectId: string,
  previewId: string,
  input: {
    customerVisible?: boolean;
    staffRecommended?: boolean;
    finalDirection?: boolean;
  }
): Promise<DesignPreview> {
  const raw = await client.patch<unknown>(
    `/api/projects/${encodeURIComponent(projectId)}/design-previews/${encodeURIComponent(previewId)}/sharing`,
    input
  );
  return z.object({ preview: DesignPreviewSchema }).parse(raw).preview;
}


export const ProjectReviewItemSchema = z.object({
  id: z.string(),
  project_id: z.string(),
  title: nullableString,
  status: nullableString,
  internal_notes: nullableString,
  customer_explanation: nullableString,
  source_type: nullableString,
  source_ref: nullableString,
  source_title: nullableString,
  source_url: nullableString,
  source_document_id: nullableString,
  supporting_document_id: nullableString,
  customer_visible: z.union([z.boolean(), z.number()]).nullish(),
  acknowledgement_required: z.union([z.boolean(), z.number()]).nullish(),
  blocking: z.union([z.boolean(), z.number()]).nullish(),
  resolution_notes: nullableString,
  review_kind: nullableString,
  technical_product: nullableString,
  technical_manufacturer: nullableString,
  technical_model: nullableString,
  verified_requirement: nullableString,
  source_version: nullableString,
  source_section: nullableString,
  project_implication: nullableString,
  cost_schedule_implication: nullableString,
  evidence_type: nullableString,
  selection_package_id: nullableString,
  selection_item_id: nullableString,
  ai_run_id: nullableString,
  created_at: nullableString,
  updated_at: nullableString,
  resolved_at: nullableString,
  resolved_by: nullableString
}).passthrough();

export type ProjectReviewItem = z.infer<typeof ProjectReviewItemSchema>;

export async function getSelections(projectId?: string): Promise<SelectionsResponse> {
  const query = projectId ? `?projectId=${encodeURIComponent(projectId)}` : '';
  const raw = await client.get<unknown>(`/api/selections${query}`);
  return SelectionsResponseSchema.parse(raw);
}

export async function getSelection(selectionId: string): Promise<Selection> {
  const raw = await client.get<unknown>(`/api/selections/${encodeURIComponent(selectionId)}`);
  return z.object({ selection: SelectionSchema }).parse(raw).selection;
}

export async function getDecisionContext(projectId: string, selectionId?: string): Promise<DecisionContextSource[]> {
  const query = selectionId ? `?selectionId=${encodeURIComponent(selectionId)}` : '';
  const raw = await client.get<unknown>(`/api/projects/${encodeURIComponent(projectId)}/decision-context${query}`);
  return z.object({ sources: z.array(DecisionContextSourceSchema).default([]) }).parse(raw).sources;
}

export async function addDecisionContext(projectId: string, input: {
  selectionId?: string | null;
  sourceType?: 'Paste' | 'Email' | 'Text Message' | 'Text File' | 'Other';
  title?: string | null;
  sourceName?: string | null;
  text: string;
}): Promise<DecisionContextSource> {
  const raw = await client.post<unknown>(`/api/projects/${encodeURIComponent(projectId)}/decision-context`, input);
  return z.object({ source: DecisionContextSourceSchema }).parse(raw).source;
}

export async function deleteDecisionContext(sourceId: string): Promise<{ deleted: boolean; id: string }> {
  const raw = await client.delete<unknown>(`/api/decision-context/${encodeURIComponent(sourceId)}`);
  return z.object({ deleted: z.boolean(), id: z.string() }).parse(raw);
}

export async function analyzeSelectionDecisionContext(selectionId: string): Promise<{
  analysis: SelectionContextAnalysis;
  provider?: string | null;
  model?: string | null;
}> {
  const raw = await client.post<unknown>(`/api/selections/${encodeURIComponent(selectionId)}/decision-context/analyze`, {});
  return z.object({
    analysis: SelectionContextAnalysisSchema,
    provider: nullableString,
    model: nullableString
  }).passthrough().parse(raw);
}

export async function generateSelections(projectId: string, estimateId: string): Promise<Record<string, unknown>> {
  return client.post<Record<string, unknown>>(`/api/projects/${encodeURIComponent(projectId)}/generate-selections`, { estimateId });
}

export async function createSelection(input: {
  projectId: string;
  room: string;
  description: string;
  allowanceAmount?: number;
  unitCost?: number;
  quantity?: number | null;
  status?: string;
  builderNotes?: string | null;
  customerVisible?: boolean;
}): Promise<Selection> {
  const raw = await client.post<unknown>('/api/selections', input);
  return z.object({ selection: SelectionSchema }).parse(raw).selection;
}

export async function updateSelection(selectionId: string, input: {
  room?: string;
  description?: string;
  allowanceAmount?: number;
  unitCost?: number;
  quantity?: number | null;
  status?: string;
  builderNotes?: string | null;
  customerInstructions?: string | null;
  requiredBeforeContract?: boolean;
  decisionType?: 'product' | 'visual' | 'layout' | 'placement' | 'combined';
  selectionTemplate?: string | null;
  needsProductCode?: boolean;
  needsFinishChoice?: boolean;
  needsLayoutChoice?: boolean;
  needsPlacementChoice?: boolean;
  needsDimensions?: boolean;
  needsPhotoReference?: boolean;
  allowAiProductMatch?: boolean;
  allowAiVisualExamples?: boolean;
  allowAiLayoutGenerator?: boolean;
  allowAiPlacementGenerator?: boolean;
  staffIncompleteOverride?: boolean;
  customerFinishChoice?: string | null;
  customerLayoutChoice?: string | null;
  customerPlacementChoice?: string | null;
  customerDimensions?: string | null;
  customerProductDescription?: string | null;
  customerProductCode?: string | null;
  customerProductUrl?: string | null;
  altiosChoose?: boolean;
  verifiedPrice?: number | null;
  customerVisible?: boolean;
}): Promise<Selection> {
  const raw = await client.patch<unknown>(`/api/selections/${encodeURIComponent(selectionId)}`, input);
  return z.object({ selection: SelectionSchema }).parse(raw).selection;
}

export async function rebindSelectionSource(selectionId: string, input: {
  estimateId: string;
  lineId: string;
}): Promise<Selection> {
  const raw = await client.patch<unknown>(`/api/selections/${encodeURIComponent(selectionId)}/source-binding`, input);
  return z.object({ selection: SelectionSchema }).parse(raw).selection;
}

export async function deleteSelection(selectionId: string): Promise<unknown> {
  return client.delete<unknown>(`/api/selections/${encodeURIComponent(selectionId)}`);
}

export async function createSelectionOption(selectionId: string, input: {
  description: string;
  productCode?: string | null;
  price?: number;
  position?: number;
}): Promise<SelectionOption> {
  const raw = await client.post<unknown>(`/api/selections/${encodeURIComponent(selectionId)}/options`, input);
  return z.object({ option: SelectionOptionSchema }).parse(raw).option;
}

export async function updateSelectionOption(optionId: string, input: {
  description?: string;
  productCode?: string | null;
  price?: number;
  position?: number;
}): Promise<SelectionOption> {
  const raw = await client.patch<unknown>(`/api/selection-options/${encodeURIComponent(optionId)}`, input);
  return z.object({ option: SelectionOptionSchema }).parse(raw).option;
}

export async function deleteSelectionOption(optionId: string): Promise<unknown> {
  return client.delete<unknown>(`/api/selection-options/${encodeURIComponent(optionId)}`);
}

export async function searchSelectionProducts(selectionId: string, refinement = ''): Promise<SelectionProductCandidate[]> {
  const raw = await client.post<unknown>(`/api/selections/${encodeURIComponent(selectionId)}/product-search`, { refinement });
  return z.object({ candidates: z.array(SelectionProductCandidateSchema).default([]) }).parse(raw).candidates;
}

export async function chooseSelectionProduct(selectionId: string, candidateId: string): Promise<Record<string, unknown>> {
  return client.post<Record<string, unknown>>(`/api/selections/${encodeURIComponent(selectionId)}/choose-product`, { candidateId });
}

export async function addSelectionToEstimate(selectionId: string, estimateId?: string): Promise<Record<string, unknown>> {
  return client.post<Record<string, unknown>>(`/api/selections/${encodeURIComponent(selectionId)}/add-to-estimate`, estimateId ? { estimateId } : {});
}

export async function sendSelectionMessage(selectionId: string, input: {
  message: string;
  needsResponse?: boolean;
  important?: boolean;
}): Promise<SelectionMessage[]> {
  const raw = await client.post<unknown>(`/api/selections/${encodeURIComponent(selectionId)}/messages`, input);
  return z.object({ messages: z.array(SelectionMessageSchema).default([]) }).passthrough().parse(raw).messages;
}

export async function getSelectionPackage(projectId: string): Promise<SelectionPackageState> {
  const raw = await client.get<unknown>(`/api/projects/${encodeURIComponent(projectId)}/selection-package`);
  return SelectionPackageStateSchema.parse(raw);
}

export async function reviewSelectionPackage(packageId: string, action: 'start' | 'changes' | 'approve', note?: string): Promise<SelectionPackage> {
  const raw = await client.patch<unknown>(`/api/selection-packages/${encodeURIComponent(packageId)}`, { action, note: note || null });
  return z.object({ package: SelectionPackageSchema }).parse(raw).package;
}

export async function getProjectReview(projectId: string): Promise<{ items: ProjectReviewItem[]; events: Array<Record<string, unknown>> }> {
  const raw = await client.get<unknown>(`/api/project-review?projectId=${encodeURIComponent(projectId)}`);
  return z.object({
    items: z.array(ProjectReviewItemSchema).default([]),
    events: z.array(z.record(z.string(), z.unknown())).default([])
  }).parse(raw);
}

export async function updateProjectReviewItem(itemId: string, input: Record<string, unknown>): Promise<ProjectReviewItem> {
  const raw = await client.patch<unknown>(`/api/project-review/${encodeURIComponent(itemId)}`, input);
  return z.object({ item: ProjectReviewItemSchema }).parse(raw).item;
}

export async function createProjectReviewItem(projectId: string, input: Record<string, unknown>): Promise<ProjectReviewItem> {
  const raw = await client.post<unknown>('/api/project-review', { projectId, ...input });
  return z.object({ item: ProjectReviewItemSchema }).parse(raw).item;
}

export const ProjectHistoryEventSchema = z.object({
  id: z.string(),
  project_id: nullableString,
  entity_type: nullableString,
  entity_id: nullableString,
  event_type: nullableString,
  actor_type: nullableString,
  actor_identity: nullableString,
  source: nullableString,
  summary: nullableString,
  before: z.unknown().nullish(),
  after: z.unknown().nullish(),
  changedFields: z.array(z.string()).default([]),
  created_at: nullableString
}).passthrough();

export type ProjectHistoryEvent = z.infer<typeof ProjectHistoryEventSchema>;

export async function getProjectHistory(projectId: string, limit = 500): Promise<ProjectHistoryEvent[]> {
  const raw = await client.get<unknown>(`/api/projects/${encodeURIComponent(projectId)}/history?limit=${encodeURIComponent(String(limit))}`);
  return z.object({ history: z.array(ProjectHistoryEventSchema).default([]) }).parse(raw).history;
}

export function productThumbnailUrl(productUrl?: string | null, imageUrl?: string | null): string | null {
  if (!productUrl && !imageUrl) return null;
  const params = new URLSearchParams();
  if (productUrl) params.set('productUrl', productUrl);
  if (imageUrl) params.set('imageUrl', imageUrl);
  return `${DEFAULT_API_BASE_URL}/api/public/product-thumbnail?${params.toString()}`;
}

export async function permanentlyDeleteDocument(documentId: string, confirmName: string): Promise<unknown> {
  return client.post<unknown>(`/api/documents/${encodeURIComponent(documentId)}/purge`, { confirmName });
}

export function documentDownloadUrl(documentId: string, inline = false): string {
  const suffix = inline ? '?inline=1' : '';
  return `${DEFAULT_API_BASE_URL}/api/documents/${encodeURIComponent(documentId)}/download${suffix}`;
}

export async function getMessageThreads(projectId?: string): Promise<MessageThread[]> {
  const query = projectId ? `?projectId=${encodeURIComponent(projectId)}` : '';
  const raw = await client.get<unknown>(`/api/message-center/threads${query}`);
  return MessageThreadsResponseSchema.parse(raw).threads;
}

export async function getMessageNotifications(projectId?: string): Promise<{ notifications: MessageNotification[]; unreadCount: number }> {
  const params = new URLSearchParams({ section: 'messages', limit: '100' });
  if (projectId) params.set('projectId', projectId);
  const raw = await client.get<unknown>(`/api/notifications?${params.toString()}`);
  const parsed = MessageNotificationsResponseSchema.parse(raw);
  return { notifications: parsed.notifications, unreadCount: parsed.unread_count };
}

export async function getMessagePins(): Promise<MessagePin[]> {
  const raw = await client.get<unknown>('/api/message-center/pins');
  return z.object({ pins: z.array(MessagePinSchema).default([]) }).parse(raw).pins;
}

export async function getMessageLifecycle(kind: 'task' | 'selection', threadId: string): Promise<MessageLifecycle> {
  const params = new URLSearchParams({ entityType: kind, entityId: threadId });
  const raw = await client.get<unknown>(`/api/message-center/lifecycle?${params.toString()}`);
  return MessageLifecycleSchema.parse(raw);
}

export async function getThreadMessages(kind: 'task' | 'selection', threadId: string): Promise<ThreadMessage[]> {
  if (kind === 'task') {
    const raw = await client.get<unknown>(`/api/tasks/${encodeURIComponent(threadId)}/comments`);
    return z.object({ comments: z.array(ThreadMessageSchema).default([]) }).parse(raw).comments;
  }
  const raw = await client.get<unknown>(`/api/selections/${encodeURIComponent(threadId)}/collaboration`);
  return z.object({ messages: z.array(ThreadMessageSchema).default([]) }).passthrough().parse(raw).messages;
}

export async function sendThreadMessage(kind: 'task' | 'selection', threadId: string, input: {
  message: string;
  needsResponse?: boolean;
  important?: boolean;
}): Promise<{ messages: ThreadMessage[]; messageId?: string }> {
  if (kind === 'task') {
    const raw = await client.post<unknown>(`/api/tasks/${encodeURIComponent(threadId)}/comments`, {
      comment: input.message,
      needsResponse: Boolean(input.needsResponse),
      important: Boolean(input.important)
    });
    const parsed = z.object({ comments: z.array(ThreadMessageSchema).default([]), messageId: z.string().optional() }).parse(raw);
    return { messages: parsed.comments, messageId: parsed.messageId };
  }
  const raw = await client.post<unknown>(`/api/selections/${encodeURIComponent(threadId)}/messages`, {
    message: input.message,
    needsResponse: Boolean(input.needsResponse),
    important: Boolean(input.important)
  });
  const parsed = z.object({ messages: z.array(ThreadMessageSchema).default([]), messageId: z.string().optional() }).passthrough().parse(raw);
  return { messages: parsed.messages, messageId: parsed.messageId };
}

export async function toggleMessageThreadPin(kind: 'task' | 'selection', threadId: string): Promise<boolean> {
  const raw = await client.post<unknown>('/api/message-center/pins/toggle', { entityType: kind, entityId: threadId });
  return z.object({ pinned: z.boolean() }).parse(raw).pinned;
}

export async function toggleStaffMessageReaction(messageType: 'task_comment' | 'selection_message', messageId: string, reaction: 'thumbs_up' | 'check'): Promise<boolean> {
  const raw = await client.post<unknown>('/api/message-center/reactions/toggle', { messageType, messageId, reaction });
  return z.object({ active: z.boolean() }).parse(raw).active;
}

export async function markMessageThreadRead(kind: 'task' | 'selection', threadId: string, projectId?: string): Promise<number> {
  const raw = await client.post<unknown>('/api/notifications/read-thread', { entityType: kind, entityId: threadId, projectId: projectId || null });
  return z.object({ count: z.number().default(0) }).passthrough().parse(raw).count;
}

export async function acknowledgeMessageThread(kind: 'task' | 'selection', threadId: string, projectId?: string): Promise<number> {
  const raw = await client.post<unknown>('/api/notifications/acknowledge-thread', { entityType: kind, entityId: threadId, projectId: projectId || null });
  return z.object({ count: z.number().default(0) }).passthrough().parse(raw).count;
}

export async function uploadMessageAttachment(input: {
  messageType: 'task_comment' | 'selection_message';
  messageId: string;
  file: File;
  category?: string;
  caption?: string;
  saveToProjectFiles?: boolean;
}): Promise<unknown> {
  const body = new FormData();
  body.append('messageType', input.messageType);
  body.append('messageId', input.messageId);
  body.append('file', input.file);
  body.append('category', input.category || 'Reference');
  body.append('caption', input.caption || '');
  body.append('saveToProjectFiles', input.saveToProjectFiles === false ? '0' : '1');
  return client.postForm<unknown>('/api/message-center/attachments/upload', body);
}

export async function linkMessageAttachment(input: {
  messageType: 'task_comment' | 'selection_message';
  messageId: string;
  documentId: string;
  category?: string;
  caption?: string;
}): Promise<unknown> {
  return client.post<unknown>('/api/message-center/attachments/link', {
    messageType: input.messageType,
    messageId: input.messageId,
    documentId: input.documentId,
    category: input.category || 'Reference',
    caption: input.caption || ''
  });
}

export function messageAttachmentDownloadUrl(attachmentId: string): string {
  return `${DEFAULT_API_BASE_URL}/api/message-center/attachments/${encodeURIComponent(attachmentId)}/download`;
}

export async function getProjectActivity(projectId?: string): Promise<ProjectActivity[]> {
  const query = projectId ? `?projectId=${encodeURIComponent(projectId)}` : '';
  const raw = await client.get<unknown>(`/api/project-activity${query}`);
  return ProjectActivityResponseSchema.parse(raw).entries;
}

export async function createProjectActivity(input: {
  projectId: string;
  entryType: 'Communication' | 'JobNote';
  channel: string;
  direction?: string | null;
  contactRole?: string | null;
  contactName?: string | null;
  subject?: string | null;
  body: string;
  occurredAt?: string | null;
  customerVisible?: boolean;
}): Promise<ProjectActivity> {
  const raw = await client.post<unknown>('/api/project-activity', input);
  return z.object({ entry: ProjectActivitySchema }).parse(raw).entry;
}

export async function requestPortalLogin(email: string): Promise<PortalLoginResponse> {
  const raw = await client.post<unknown>('/api/portal/login', { email });
  return PortalLoginResponseSchema.parse(raw);
}

export const PortalSelectionDisclosuresSchema = z.object({
  complete: z.boolean().default(false)
}).passthrough();

export const PortalEstimateLineSchema = z.object({
  id: z.string(), description: z.string(), quantity: z.number(),
  usageUnit: nullableString, rate: z.number(), total: z.number()
});
export const PortalEstimateChangeSchema = z.object({
  id: z.string(),
  change: z.enum(['initial', 'added', 'modified', 'unchanged', 'removed']),
  previous: PortalEstimateLineSchema.omit({ id: true }).nullish()
});
export const PortalEstimateSchema = z.object({
  id: z.string(), title: z.string(), status: z.string(),
  approvalRequestId: z.string(), approvalStatus: z.string(),
  versionId: z.string(), versionNumber: z.number(),
  previousVersionNumber: nullableNumber, previousTotal: nullableNumber,
  isCurrentVersion: z.boolean(), canApprove: z.boolean(),
  scopeOfWork: z.string(), total: z.number(),
  lineItems: z.array(PortalEstimateLineSchema).default([]),
  lineChanges: z.array(PortalEstimateChangeSchema).default([]),
  removedLines: z.array(PortalEstimateLineSchema.extend({ change: z.literal('removed') })).default([]),
  customerNotes: z.array(z.object({
    id: z.string(), email: z.string(), text: z.string(), createdAt: nullableString
  })).default([])
}).passthrough();
export type PortalEstimate = z.infer<typeof PortalEstimateSchema>;

export const PortalProjectDetailSchema = z.object({
  project: ProjectSchema,
  projectCustomer: z.unknown().nullish(),
  tasks: z.array(TaskSchema).default([]),
  appointments: z.array(AppointmentSchema).default([]),
  unavailability: z.array(ProjectUnavailabilitySchema).default([]),
  phaseProgress: PortalPhaseProgressSchema.nullish(),
  closeout: PortalCloseoutSummarySchema.nullish(),
  selections: z.array(SelectionSchema).default([]),
  selectionPackage: SelectionPackageStateSchema.nullish(),
  layout: SelectionLayoutSchema.nullish(),
  selectionDisclosures: PortalSelectionDisclosuresSchema.nullish(),
  contracts: z.array(ContractSchema).default([]),
  documents: z.array(DocumentSchema).default([]),
  designPreviews: z.array(DesignPreviewSchema).default([]),
  designReferences: z.array(SelectionDesignReferenceSchema).default([]),
  milestones: z.array(MilestoneSchema).default([]),
  changeOrders: z.array(ChangeOrderSchema).default([]),
  estimate: PortalEstimateSchema.nullish(),
  reviewItems: z.array(ProjectReviewItemSchema).default([]),
  acknowledgements: z.array(z.record(z.string(), z.unknown())).default([]),
  financialSummary: FinancialSummarySchema.nullish()
}).passthrough();

export type PortalProjectDetail = z.infer<typeof PortalProjectDetailSchema>;

export async function portalApproveProjectLayout(projectId: string, email: string): Promise<SelectionLayout> {
  const raw = await client.post<unknown>(
    `/api/portal/projects/${encodeURIComponent(projectId)}/layout/approve`,
    { email }
  );
  return z.object({ layout: SelectionLayoutSchema }).parse(raw).layout;
}

export async function portalApproveEstimate(projectId: string, email: string, approvalRequestId: string): Promise<PortalEstimate> {
  const raw = await client.post<unknown>('/api/portal/estimate/approve', { projectId, email, approvalRequestId });
  return z.object({ estimate: PortalEstimateSchema }).parse(raw).estimate;
}

export async function portalAddEstimateNote(projectId: string, email: string, estimateId: string, versionNumber: number, note: string): Promise<PortalEstimate> {
  const raw = await client.post<unknown>('/api/portal/estimate/note', { projectId, email, estimateId, versionNumber, note });
  return z.object({ estimate: PortalEstimateSchema }).parse(raw).estimate;
}

export async function requestPortalProject(projectId: string, email: string): Promise<PortalProjectDetail> {
  const raw = await client.get<unknown>(`/api/portal/project/${encodeURIComponent(projectId)}?email=${encodeURIComponent(email)}`);
  return PortalProjectDetailSchema.parse(raw);
}

export function portalDocumentUrl(documentId: string, email: string, download = false): string {
  const params = new URLSearchParams({ email });
  if (download) params.set('download', '1');
  return `${DEFAULT_API_BASE_URL}/api/portal/documents/${encodeURIComponent(documentId)}?${params.toString()}`;
}


export type PortalMessageThreadKind = 'task' | 'selection';
export type PortalMessageType = 'task_comment' | 'selection_message';

export async function portalGetTaskComments(taskId: string, email: string): Promise<ThreadMessage[]> {
  const raw = await client.get<unknown>(`/api/portal/tasks/${encodeURIComponent(taskId)}/comments?email=${encodeURIComponent(email)}`);
  return z.object({ comments: z.array(ThreadMessageSchema).default([]) }).parse(raw).comments;
}

export async function portalSendTaskComment(taskId: string, input: {
  email: string;
  comment: string;
  important?: boolean;
  needsResponse?: boolean;
}): Promise<{ comments: ThreadMessage[]; messageId?: string | null }> {
  const raw = await client.post<unknown>(`/api/portal/tasks/${encodeURIComponent(taskId)}/comments`, input);
  return z.object({
    comments: z.array(ThreadMessageSchema).default([]),
    messageId: nullableString
  }).parse(raw);
}

export async function portalGetNotifications(projectId: string, email: string): Promise<{ notifications: MessageNotification[]; unreadCount: number }> {
  const params = new URLSearchParams({ projectId, email, limit: '100' });
  const raw = await client.get<unknown>(`/api/portal/notifications?${params.toString()}`);
  const parsed = MessageNotificationsResponseSchema.parse(raw);
  return { notifications: parsed.notifications, unreadCount: parsed.unread_count };
}

export async function portalMarkThreadRead(projectId: string, email: string, kind: PortalMessageThreadKind, threadId: string): Promise<number> {
  const raw = await client.post<unknown>('/api/portal/notifications/read-thread', {
    projectId, email, entityType: kind, entityId: threadId
  });
  return z.object({ count: z.number().default(0) }).parse(raw).count;
}

export async function portalAcknowledgeMessageThread(projectId: string, email: string, kind: PortalMessageThreadKind, threadId: string): Promise<number> {
  const raw = await client.post<unknown>('/api/portal/notifications/acknowledge-thread', {
    projectId, email, entityType: kind, entityId: threadId
  });
  return z.object({ count: z.number().default(0) }).parse(raw).count;
}

export async function portalGetMessagePins(projectId: string, email: string): Promise<MessagePin[]> {
  const params = new URLSearchParams({ projectId, email });
  const raw = await client.get<unknown>(`/api/portal/message-center/pins?${params.toString()}`);
  return z.object({ pins: z.array(MessagePinSchema).default([]) }).parse(raw).pins;
}

export async function portalToggleMessagePin(projectId: string, email: string, kind: PortalMessageThreadKind, threadId: string): Promise<boolean> {
  const raw = await client.post<unknown>('/api/portal/message-center/pins/toggle', {
    projectId, email, entityType: kind, entityId: threadId
  });
  return z.object({ pinned: z.boolean() }).parse(raw).pinned;
}

export async function portalGetMessageLifecycle(projectId: string, email: string, kind: PortalMessageThreadKind, threadId: string): Promise<MessageLifecycle> {
  const params = new URLSearchParams({ projectId, email, entityType: kind, entityId: threadId });
  const raw = await client.get<unknown>(`/api/portal/message-center/lifecycle?${params.toString()}`);
  return MessageLifecycleSchema.parse(raw);
}

export async function portalToggleMessageReaction(projectId: string, email: string, messageType: PortalMessageType, messageId: string, reaction: 'thumbs_up' | 'check'): Promise<boolean> {
  const raw = await client.post<unknown>('/api/portal/message-center/reactions/toggle', {
    projectId, email, messageType, messageId, reaction
  });
  return z.object({ active: z.boolean() }).parse(raw).active;
}

export async function portalEditMessage(projectId: string, email: string, messageType: PortalMessageType, messageId: string, body: string): Promise<ThreadMessage> {
  const raw = await client.patch<unknown>('/api/portal/message-center/message', {
    projectId, email, messageType, messageId, body
  });
  return z.object({ message: ThreadMessageSchema }).parse(raw).message;
}

export async function portalHideMessage(projectId: string, email: string, messageType: PortalMessageType, messageId: string): Promise<void> {
  await client.post<unknown>('/api/portal/message-center/hide', {
    projectId, email, messageType, messageId
  });
}

export async function portalUploadMessageAttachment(input: {
  projectId: string;
  email: string;
  messageType: PortalMessageType;
  messageId: string;
  file: File;
  category?: string;
  caption?: string;
  saveToProjectFiles?: boolean;
}): Promise<unknown> {
  const body = new FormData();
  body.append('projectId', input.projectId);
  body.append('email', input.email);
  body.append('messageType', input.messageType);
  body.append('messageId', input.messageId);
  body.append('file', input.file);
  body.append('category', input.category || 'Reference');
  body.append('caption', input.caption || '');
  body.append('saveToProjectFiles', input.saveToProjectFiles ? '1' : '0');
  return client.postForm<unknown>('/api/portal/message-center/attachments/upload', body);
}

export async function portalLinkMessageAttachment(input: {
  projectId: string;
  email: string;
  messageType: PortalMessageType;
  messageId: string;
  documentId: string;
  category?: string;
  caption?: string;
}): Promise<unknown> {
  return client.post<unknown>('/api/portal/message-center/attachments/link', input);
}

export function portalMessageAttachmentUrl(attachmentId: string, email: string, download = true): string {
  const params = new URLSearchParams({ email });
  if (download) params.set('download', '1');
  return `${DEFAULT_API_BASE_URL}/api/portal/message-center/attachments/${encodeURIComponent(attachmentId)}?${params.toString()}`;
}

export async function portalCreateAppointment(input: {
  projectId: string;
  email: string;
  appointmentType: string;
  title: string;
  startsAt: string;
  endsAt: string;
  timezone?: string;
  location?: string;
  customerNotes?: string;
  reminderTiming?: string;
  reminderDelivery?: string;
}): Promise<{ appointments: Appointment[]; unavailability: ProjectUnavailability[] }> {
  const raw = await client.post<unknown>('/api/portal/appointments', input);
  return z.object({ appointments: z.array(AppointmentSchema).default([]), unavailability: z.array(ProjectUnavailabilitySchema).default([]) }).parse(raw);
}

export async function portalUpdateAppointment(appointmentId: string, input: {
  email: string;
  action: 'confirm' | 'reschedule' | 'cancel' | 'reminder';
  startsAt?: string;
  endsAt?: string;
  location?: string;
  customerNotes?: string;
  reminderTiming?: string;
  reminderDelivery?: string;
}): Promise<{ appointments: Appointment[]; unavailability: ProjectUnavailability[] }> {
  const raw = await client.patch<unknown>(`/api/portal/appointments/${encodeURIComponent(appointmentId)}`, input);
  return z.object({ appointments: z.array(AppointmentSchema).default([]), unavailability: z.array(ProjectUnavailabilitySchema).default([]) }).parse(raw);
}

export async function portalCreateUnavailability(input: {
  projectId: string;
  email: string;
  startsAt: string;
  endsAt: string;
  allDay?: boolean;
  note?: string;
}): Promise<{ appointments: Appointment[]; unavailability: ProjectUnavailability[] }> {
  const raw = await client.post<unknown>('/api/portal/unavailability', input);
  return z.object({ appointments: z.array(AppointmentSchema).default([]), unavailability: z.array(ProjectUnavailabilitySchema).default([]) }).parse(raw);
}

export async function portalRemoveUnavailability(id: string, email: string): Promise<{ appointments: Appointment[]; unavailability: ProjectUnavailability[] }> {
  const raw = await client.patch<unknown>(`/api/portal/unavailability/${encodeURIComponent(id)}/remove`, { email });
  return z.object({ appointments: z.array(AppointmentSchema).default([]), unavailability: z.array(ProjectUnavailabilitySchema).default([]) }).parse(raw);
}

export async function portalExplain(projectId: string, email: string, sourceText: string, contextLabel: string): Promise<{ explanation: string; sourceOfTruth?: string | null }> {
  const raw = await client.post<unknown>(`/api/portal/projects/${encodeURIComponent(projectId)}/explain`, { email, sourceText, contextLabel });
  const parsed = z.object({ explanation: z.string(), source_of_truth: nullableString }).parse(raw);
  return { explanation: parsed.explanation, sourceOfTruth: parsed.source_of_truth };
}

export const PortalPaymentSessionSchema = z.object({
  paymentSessionId: z.string(),
  amount: z.number(),
  currency: z.string(),
  invoiceNumber: nullableString,
  description: nullableString,
  methodCode: z.enum(['ach', 'card']),
  accountId: z.string(),
  apiKey: z.string(),
  customer: z.object({
    name: nullableString,
    email: nullableString,
    phone: nullableString
  }).passthrough()
}).passthrough();

export type PortalPaymentSession = z.infer<typeof PortalPaymentSessionSchema>;

export const PortalPaymentVerificationSchema = z.object({
  verified: z.boolean(),
  succeeded: z.boolean(),
  pending: z.boolean(),
  paymentId: nullableString,
  paymentSessionId: nullableString,
  status: nullableString,
  amount: nullableNumber,
  currency: nullableString,
  paymentMethod: nullableString
}).passthrough();

export type PortalPaymentVerification = z.infer<typeof PortalPaymentVerificationSchema>;

export async function portalCreatePaymentSession(
  eventType: 'milestone' | 'change-order',
  eventId: string,
  input: {
    email: string;
    methodCode: 'ach' | 'card';
  }
): Promise<PortalPaymentSession> {
  const raw = await client.post<unknown>(
    `/api/portal/payment-session/${eventType}/${encodeURIComponent(eventId)}`,
    input
  );

  return PortalPaymentSessionSchema.parse(raw);
}

export async function portalVerifyPayment(
  eventType: 'milestone' | 'change-order',
  eventId: string,
  input: {
    email: string;
    paymentId: string;
    paymentSessionId: string;
  }
): Promise<PortalPaymentVerification> {
  const raw = await client.post<unknown>(
    `/api/portal/payment-session/${eventType}/${encodeURIComponent(eventId)}/verify`,
    input
  );

  return PortalPaymentVerificationSchema.parse(raw);
}
export async function portalRecordPaymentElection(eventType: 'milestone' | 'change-order', eventId: string, input: {
  email: string;
  methodCode: 'ach' | 'card' | 'check' | 'wire' | 'cash' | 'other';
  methodLabel?: string | null;
}): Promise<{ election: PaymentElection; paymentUrl?: string | null; paymentLinkAvailable?: boolean }> {
  const raw = await client.post<unknown>(`/api/portal/payment-elections/${eventType}/${encodeURIComponent(eventId)}`, input);
  return z.object({
    election: PaymentElectionSchema,
    paymentUrl: nullableString,
    paymentLinkAvailable: z.boolean().optional()
  }).passthrough().parse(raw);
}

export function portalFinancialStatementBlob(projectId: string, email: string, filter: 'all' | 'invoices' | 'payments' | 'change-orders' = 'all'): Promise<Blob> {
  return downloadBlob(`/api/portal/projects/${encodeURIComponent(projectId)}/financial-statement`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/pdf' },
    body: JSON.stringify({ email, filter })
  });
}

export async function portalRefineDesignPreview(
  projectId: string,
  selectionId: string,
  previewId: string,
  input: {
    email: string;
    prompt: string;
    referenceDocumentIds?: string[];
  }
): Promise<DesignPreview> {
  const raw = await client.post<unknown>(
    `/api/portal/projects/${encodeURIComponent(projectId)}/selections/${encodeURIComponent(selectionId)}/design-previews/${encodeURIComponent(previewId)}/refine`,
    input
  );
  return z.object({ preview: DesignPreviewSchema }).parse(raw).preview;
}

export async function portalSearchSelectionProducts(selectionId: string, email: string, refinement = ''): Promise<SelectionProductCandidate[]> {
  const raw = await client.post<unknown>(`/api/portal/selections/${encodeURIComponent(selectionId)}/product-search`, { email, refinement });
  return z.object({ candidates: z.array(SelectionProductCandidateSchema).default([]) }).parse(raw).candidates;
}

export async function portalClearSelectionSearch(selectionId: string, email: string): Promise<Selection[]> {
  const raw = await client.post<unknown>(`/api/portal/selections/${encodeURIComponent(selectionId)}/product-search/clear`, { email });
  return z.object({ selections: z.array(SelectionSchema).default([]) }).parse(raw).selections;
}

export async function portalChooseSelectionProduct(selectionId: string, email: string, candidateId: string): Promise<Selection[]> {
  const raw = await client.post<unknown>(`/api/portal/selections/${encodeURIComponent(selectionId)}/choose-product`, { email, candidateId });
  return z.object({ selections: z.array(SelectionSchema).default([]) }).parse(raw).selections;
}

export async function portalSubmitSelectionProduct(selectionId: string, input: {
  email: string;
  description?: string;
  productCode?: string;
  productUrl?: string;
  altiosChoose?: boolean;
}): Promise<Selection[]> {
  const raw = await client.post<unknown>(`/api/portal/selections/${encodeURIComponent(selectionId)}/product`, input);
  return z.object({ selections: z.array(SelectionSchema).default([]) }).parse(raw).selections;
}

export async function portalSubmitSelectionDecision(selectionId: string, input: {
  email: string;
  finishChoice?: string | null;
  layoutChoice?: string | null;
  placementChoice?: string | null;
  dimensions?: string | null;
  altiosChoose?: boolean;
}): Promise<Selection[]> {
  const raw = await client.post<unknown>(`/api/portal/selections/${encodeURIComponent(selectionId)}/decision`, input);
  return z.object({ selections: z.array(SelectionSchema).default([]) }).parse(raw).selections;
}

export async function portalChooseSelectionOption(selectionId: string, email: string, optionId: string): Promise<Selection[]> {
  const raw = await client.post<unknown>(`/api/portal/selections/${encodeURIComponent(selectionId)}/choose`, { email, optionId });
  return z.object({ selections: z.array(SelectionSchema).default([]) }).parse(raw).selections;
}

export async function portalApproveSelectionOverage(selectionId: string, email: string): Promise<Selection[]> {
  const raw = await client.post<unknown>(`/api/portal/selections/${encodeURIComponent(selectionId)}/approve-overage`, { email });
  return z.object({ selections: z.array(SelectionSchema).default([]) }).parse(raw).selections;
}

export async function portalSendSelectionMessage(selectionId: string, input: {
  email: string;
  message: string;
  needsResponse?: boolean;
  important?: boolean;
}): Promise<Selection[]> {
  const raw = await client.post<unknown>(`/api/portal/selections/${encodeURIComponent(selectionId)}/messages`, input);
  return z.object({ selections: z.array(SelectionSchema).default([]) }).passthrough().parse(raw).selections;
}


export async function portalSendSelectionThreadMessage(selectionId: string, input: {
  email: string;
  message: string;
  needsResponse?: boolean;
  important?: boolean;
}): Promise<{ selections: Selection[]; messageId?: string | null }> {
  const raw = await client.post<unknown>(`/api/portal/selections/${encodeURIComponent(selectionId)}/messages`, input);
  return z.object({
    selections: z.array(SelectionSchema).default([]),
    messageId: nullableString
  }).passthrough().parse(raw);
}

export async function portalSetTaskComplete(taskId: string, email: string, completed: boolean): Promise<ProjectTask[]> {
  const raw = await client.post<unknown>(`/api/portal/tasks/${encodeURIComponent(taskId)}/complete`, { email, completed });
  return z.object({ tasks: z.array(TaskSchema).default([]) }).parse(raw).tasks;
}

export async function portalSubmitSelectionPackage(projectId: string, email: string): Promise<{ package: SelectionPackage; state: SelectionPackageState }> {
  const raw = await client.post<unknown>(`/api/portal/projects/${encodeURIComponent(projectId)}/selection-package/submit`, { email });
  return z.object({ package: SelectionPackageSchema, state: SelectionPackageStateSchema }).parse(raw);
}

export async function portalAcknowledgeSelectionDisclosures(projectId: string, email: string): Promise<Record<string, unknown>> {
  return client.post<Record<string, unknown>>(`/api/portal/projects/${encodeURIComponent(projectId)}/acknowledgements/selections`, {
    email,
    materialAccepted: true,
    warrantyAccepted: true
  });
}

export const portalStorage = {
  email: 'portal_email',
  sessionActive: 'portal_session_active',
  projectId: 'portal_project_id',
  activeSection: 'portal_active_section',
  desktopView: 'altiosPortalDesktopView',
  moneySection: 'portal_money_section'
} as const;

// -----------------------------------------------------------------------------
// Altios Assist Ã¢â‚¬â€ read-only contextual staff AI
// -----------------------------------------------------------------------------
export const AiAssistantModeSchema = z.enum(['brief', 'blockers', 'draft-customer-update', 'financial', 'ask']);
export type AiAssistantMode = z.infer<typeof AiAssistantModeSchema>;

export const AiAssistantEvidenceSchema = z.object({
  kind: z.string(),
  label: z.string(),
  value: z.union([z.string(), z.number(), z.boolean()]),
  route: z.string().nullish()
}).passthrough();
export type AiAssistantEvidence = z.infer<typeof AiAssistantEvidenceSchema>;

export const AiAssistantResponseSchema = z.object({
  answer: z.string(),
  mode: AiAssistantModeSchema,
  scope: z.enum(['project', 'portfolio']),
  projectId: nullableString,
  model: z.string(),
  provider: z.string(),
  gatewayId: nullableString,
  generatedAt: z.string(),
  evidence: z.array(AiAssistantEvidenceSchema).default([]),
  authority: z.string()
}).passthrough();
export type AiAssistantResponse = z.infer<typeof AiAssistantResponseSchema>;

export async function runAiAssistant(input: {
  projectId?: string | null;
  mode: AiAssistantMode;
  prompt?: string;
}): Promise<AiAssistantResponse> {
  const raw = await client.post<unknown>('/api/ai/assistant', {
    projectId: input.projectId || undefined,
    mode: input.mode,
    prompt: input.prompt || undefined
  });
  return AiAssistantResponseSchema.parse(raw);
}

// ============================================================
// Company operations / administration read + action layer
// Restored from the pre-React Desk without reintroducing legacy UI.
// ============================================================

export const IntakeLeadSchema = z.object({
  id: z.string(),
  first_name: nullableString,
  last_name: nullableString,
  email: nullableString,
  phone: nullableString,
  start_window: nullableString,
  wishlist: nullableString,
  status: nullableString,
  customer_id: nullableString,
  project_id: nullableString,
  zoho_customer_id: nullableString,
  detail_status: nullableString,
  detail_sent_at: nullableString,
  detail_submitted_at: nullableString,
  billing_address: nullableString,
  jobsite_address: nullableString,
  jobsite_county: nullableString,
  budget_range: nullableString,
  project_type: nullableString,
  additional_notes: nullableString,
  created_at: nullableString
}).passthrough();
export type IntakeLead = z.infer<typeof IntakeLeadSchema>;

export const DashboardStatsSchema = z.object({
  financials: z.object({ invoiced: z.number().default(0) }).default({ invoiced: 0 }),
  tasks: z.array(TaskSchema).default([]),
  messages: z.array(z.record(z.string(), z.unknown())).default([]),
  errors: z.array(z.record(z.string(), z.unknown())).default([]),
  projects: z.array(ProjectSchema).default([]),
  intake: z.array(IntakeLeadSchema).default([]),
  work: z.object({ open: z.number().default(0), overdue: z.number().default(0) }).default({ open: 0, overdue: 0 }),
  appointments: z.object({ upcoming7Days: z.number().default(0), proposed: z.number().default(0) }).default({ upcoming7Days: 0, proposed: 0 }),
  selections: z.object({ openCustomerItems: z.number().default(0) }).default({ openCustomerItems: 0 })
}).passthrough();
export type DashboardStats = z.infer<typeof DashboardStatsSchema>;

export const SimplyWiseDocumentSchema = z.object({
  id: z.string(),
  source_file_id: nullableString,
  source_provider: nullableString,
  file_name: nullableString,
  mime_type: nullableString,
  document_id: nullableString,
  project_id: nullableString,
  project_name: nullableString,
  estimate_import_id: nullableString,
  estimate_id: nullableString,
  estimate_title: nullableString,
  estimate_status: nullableString,
  status: nullableString,
  parsed_json: nullableString,
  parsed_scope_of_work: nullableString,
  parsed_notes: nullableString,
  source_total_cents: nullableNumber,
  error_detail: nullableString,
  created_at: nullableString,
  updated_at: nullableString,
  parsed: z.unknown().nullish(),
  estimate_usage: z.unknown().nullish()
}).passthrough();
export type SimplyWiseDocument = z.infer<typeof SimplyWiseDocumentSchema>;

export const CoaZohoMappingSchema = z.object({
  account_code: z.union([z.string(), z.number()]),
  zoho_item_id: z.union([z.string(), z.number()]).transform(String),
  zoho_item_name: nullableString,
  usage_unit: nullableString,
  revenue_type: nullableString,
  purchase_account_name: nullableString,
  sales_account_code: z.union([z.string(), z.number()]).nullish(),
  is_preferred: z.union([z.boolean(), z.number()]).default(0),
  status: nullableString,
  source: nullableString,
  created_at: nullableString,
  updated_at: nullableString
}).passthrough();
export type CoaZohoMapping = z.infer<typeof CoaZohoMappingSchema>;

export const EmailTemplateSchema = z.object({
  template_key: z.string(),
  label: nullableString,
  subject: nullableString,
  body: nullableString,
  placeholders: nullableString,
  created_at: nullableString,
  updated_at: nullableString
}).passthrough();
export type EmailTemplate = z.infer<typeof EmailTemplateSchema>;

export async function getDashboardStats(): Promise<DashboardStats> {
  const raw = await client.get<unknown>('/api/dashboard/stats');
  return DashboardStatsSchema.parse(raw);
}

export async function getStaffNotifications(limit = 100): Promise<{ notifications: MessageNotification[]; unreadCount: number }> {
  const raw = await client.get<unknown>(`/api/notifications?limit=${encodeURIComponent(String(limit))}`);
  const parsed = MessageNotificationsResponseSchema.parse(raw);
  return { notifications: parsed.notifications, unreadCount: parsed.unread_count };
}

export async function markStaffNotificationRead(id: string): Promise<void> {
  await client.patch(`/api/notifications/${encodeURIComponent(id)}/read`, {});
}

export async function markAllStaffNotificationsRead(): Promise<number> {
  const raw = await client.post<unknown>('/api/notifications/read-all', {});
  return z.object({ count: z.number().default(0) }).passthrough().parse(raw).count;
}

export async function getIntakeLeads(): Promise<IntakeLead[]> {
  const raw = await client.get<unknown>('/api/intake');
  return z.object({ intake: z.array(IntakeLeadSchema).default([]) }).parse(raw).intake;
}

export async function createIntakeLead(input: {
  firstName: string; lastName: string; email?: string; phone?: string; startWindow?: string; wishlist?: string;
  billingAddress?: string; jobsiteAddress?: string; jobsiteCounty?: string;
}): Promise<IntakeLead> {
  const raw = await client.post<unknown>('/api/intake', input);
  return z.object({ intake: IntakeLeadSchema }).parse(raw).intake;
}

export async function createPublicIntake(input: {
  firstName: string; lastName: string; email: string; phone: string; startWindow: string; wishlist: string;
  billingAddress: string; jobsiteAddress: string; jobsiteCounty: string;
}): Promise<IntakeLead> {
  const raw = await client.post<unknown>('/api/public/intake', input);
  return z.object({ intake: IntakeLeadSchema }).parse(raw).intake;
}

export async function deleteIntakeLead(id: string): Promise<void> {
  await client.delete(`/api/intake/${encodeURIComponent(id)}`);
}

export async function convertIntakeLead(id: string): Promise<Customer> {
  const raw = await client.post<unknown>(`/api/intake/${encodeURIComponent(id)}/convert`, {});
  return z.object({ customer: CustomerSchema }).passthrough().parse(raw).customer;
}

export async function sendIntakeDetailForm(id: string): Promise<{ status?: string; emailSent?: boolean; emailError?: string | null; link?: string }> {
  return client.post(`/api/intake/${encodeURIComponent(id)}/send-detail-form`, {});
}

export async function approveIntakeDetail(id: string, input: {
  firstName?: string; lastName?: string; email?: string; phone?: string; billingAddress?: string; jobsiteAddress?: string;
  budgetRange?: string; projectType?: string; additionalNotes?: string; jobsiteCounty?: string;
}): Promise<Record<string, unknown>> {
  return client.post(`/api/intake/${encodeURIComponent(id)}/approve-detail`, input);
}

export async function getSimplyWiseDocuments(): Promise<SimplyWiseDocument[]> {
  const raw = await client.get<unknown>('/api/simplywise');
  return z.object({ documents: z.array(SimplyWiseDocumentSchema).default([]) }).parse(raw).documents;
}

export async function getSimplyWiseDocument(id: string): Promise<SimplyWiseDocument> {
  const raw = await client.get<unknown>(`/api/simplywise/${encodeURIComponent(id)}`);
  return z.object({ document: SimplyWiseDocumentSchema }).parse(raw).document;
}

export async function pollSimplyWise(): Promise<Record<string, unknown>> {
  return client.post('/api/simplywise/poll', {});
}

export async function uploadSimplyWise(file: File, projectId?: string | null): Promise<SimplyWiseDocument> {
  const body = new FormData();
  body.append('file', file);
  if (projectId) body.append('projectId', projectId);
  const raw = await client.postForm<unknown>('/api/simplywise/upload', body);
  return z.object({ document: SimplyWiseDocumentSchema }).parse(raw).document;
}

export async function linkSimplyWiseProject(id: string, projectId: string): Promise<SimplyWiseDocument> {
  const raw = await client.patch<unknown>(`/api/simplywise/${encodeURIComponent(id)}/link`, { projectId });
  return z.object({ document: SimplyWiseDocumentSchema }).parse(raw).document;
}

export async function parseSimplyWise(id: string): Promise<SimplyWiseDocument> {
  const raw = await client.post<unknown>(`/api/simplywise/${encodeURIComponent(id)}/parse`, {});
  return z.object({ document: SimplyWiseDocumentSchema }).parse(raw).document;
}

export async function saveSimplyWiseReview(id: string, input: { items: Array<Record<string, unknown>>; scopeOfWork?: string | null; notes?: string | null }): Promise<SimplyWiseDocument> {
  const raw = await client.patch<unknown>(`/api/simplywise/${encodeURIComponent(id)}/items`, input);
  return z.object({ document: SimplyWiseDocumentSchema }).parse(raw).document;
}

export async function approveSimplyWise(id: string): Promise<Record<string, unknown>> {
  return client.post(`/api/simplywise/${encodeURIComponent(id)}/approve`, {});
}

export async function getCoaZohoMappings(includeInactive = false): Promise<CoaZohoMapping[]> {
  const suffix = includeInactive ? '?includeInactive=1' : '';
  const raw = await client.get<unknown>(`/api/item-account-mappings${suffix}`);
  return z.object({ mappings: z.array(CoaZohoMappingSchema).default([]) }).parse(raw).mappings;
}

export async function saveCoaZohoMapping(input: {
  accountCode: string | number; zohoItemId: string; zohoItemName: string; usageUnit?: string | null; revenueType?: string | null;
  purchaseAccountName?: string | null; salesAccountCode?: string | number | null; isPreferred?: boolean; status?: 'Active' | 'Inactive';
}): Promise<CoaZohoMapping> {
  const raw = await client.post<unknown>('/api/coa-zoho-item-mappings', input);
  return z.object({ mapping: CoaZohoMappingSchema }).parse(raw).mapping;
}

export async function deactivateCoaZohoMapping(accountCode: string | number, zohoItemId: string): Promise<void> {
  await client.delete(`/api/coa-zoho-item-mappings/${encodeURIComponent(String(accountCode))}/${encodeURIComponent(zohoItemId)}`);
}

export async function getChartOfAccounts(): Promise<Array<Record<string, unknown>>> {
  const raw = await client.get<unknown>('/api/chart-of-accounts');
  return z.object({ accounts: z.array(z.record(z.string(), z.unknown())).default([]) }).parse(raw).accounts;
}

export async function updatePriceBookSettings(input: Record<string, unknown>): Promise<PriceBook['settings']> {
  const raw = await client.patch<unknown>('/api/price-book/settings', input);
  return z.object({ settings: PriceBookSchema.shape.settings }).parse(raw).settings;
}

export async function seedPriceBookFromZoho(): Promise<Record<string, unknown>> {
  return client.post('/api/price-book/seed-from-zoho', {});
}

export async function importPriceBookItems(items: Array<Record<string, unknown>>): Promise<Record<string, unknown>> {
  return client.post('/api/price-book/import', { items });
}

export async function createPriceBookItem(input: Record<string, unknown>): Promise<PriceBookItem> {
  const raw = await client.post<unknown>('/api/price-book/items', input);
  return z.object({ item: PriceBookItemSchema }).parse(raw).item;
}

export async function updatePriceBookItem(id: string, input: Record<string, unknown>): Promise<PriceBookItem> {
  const raw = await client.patch<unknown>(`/api/price-book/items/${encodeURIComponent(id)}`, input);
  return z.object({ item: PriceBookItemSchema }).parse(raw).item;
}

export async function deactivatePriceBookItem(id: string): Promise<void> {
  await client.delete(`/api/price-book/items/${encodeURIComponent(id)}`);
}

export async function recordPriceBookCheck(id: string, input: Record<string, unknown>): Promise<Record<string, unknown>> {
  return client.post(`/api/price-book/items/${encodeURIComponent(id)}/price-check`, input);
}

export async function getEmailTemplates(): Promise<EmailTemplate[]> {
  const raw = await client.get<unknown>('/api/email-templates');
  return z.object({ templates: z.array(EmailTemplateSchema).default([]) }).parse(raw).templates;
}

export async function getEmailTemplate(key: string): Promise<EmailTemplate> {
  const raw = await client.get<unknown>(`/api/email-templates/${encodeURIComponent(key)}`);
  return z.object({ template: EmailTemplateSchema }).parse(raw).template;
}

export async function updateEmailTemplate(key: string, input: { subject: string; body: string }): Promise<EmailTemplate> {
  const raw = await client.patch<unknown>(`/api/email-templates/${encodeURIComponent(key)}`, input);
  return z.object({ template: EmailTemplateSchema }).parse(raw).template;
}
